/**
 * Elite Batch 7 Part 4 — Workflow Templates 623–647
 * Finance, billing, compliance, membership, and multi-currency workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 7 PART 4 — TEMPLATES 623–647
// =============================================================================

export const ELITE_BATCH7_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 623. EXPENSE REPORT SUBMISSION BOT (7 nodes)
  // ===================================================================

  "Expense Report Submission Bot": {
    summary:
      "Conversational bot that iteratively collects expense line items, processes receipts, validates completeness, and submits a formal report. A Slack/portal trigger starts the bot, AI iterates to collect items, receipt data is processed, a running total is computed via runCode, completeness is validated via condition, a formal report is generated, and a submission confirmation email is sent.",
    nodes: [
      trigger("Webhook: Expense report started", "webhook_app", {
        source: "Slack / Internal Portal",
        event: "expense.report.started",
        howItStarts:
          "Employee initiates expense report submission via Slack command or internal portal button",
        setup:
          "Configure Slack slash command or portal webhook to fire when user starts an expense report. Payload should include user identity and raw expense data.",
        inputFields: [],
      }),

      // 2. AI iterative collection
      aiNode(
        "2",
        "AI: Collect & parse expense items",
        280,
        "You are an expense report assistant. Parse the incoming payload to extract all expense line items. For each item, identify: description, amount, currency, category (travel, meals, supplies, lodging, transport, other), date, and whether a receipt image URL is present. If data is incomplete, flag missing fields. Respond JSON: {employee_email: string, employee_name: string, items: [{description: string, amount: number, currency: string, category: string, date: string, receipt_url: string, complete: boolean}], total_items: number, items_with_receipts: number, items_missing_data: number, report_title: string}",
        "Expense submission payload: {webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "items", as: "expense_items_json", type: "string" },
          { field: "total_items", as: "total_items", type: "number" },
          {
            field: "items_with_receipts",
            as: "items_with_receipts",
            type: "number",
          },
          {
            field: "items_missing_data",
            as: "items_missing_data",
            type: "number",
          },
          { field: "report_title", as: "report_title", type: "string" },
        ],
      ),

      // 3. Receipt processing
      aiNode(
        "3",
        "AI: Process & validate receipts",
        520,
        "You are a receipt validation specialist. Given expense items with receipt URLs, validate each receipt matches its claimed amount and category. Flag any discrepancies between receipt data and claimed amounts. Respond JSON: {validated_items: string, receipt_match_rate: number, flagged_discrepancies: string, receipt_summary: string}",
        "Expense items: {expense_items_json}. Items with receipts: {items_with_receipts} of {total_items}.",
        [
          { field: "validated_items", as: "validated_items", type: "string" },
          {
            field: "receipt_match_rate",
            as: "receipt_match_rate",
            type: "number",
          },
          {
            field: "flagged_discrepancies",
            as: "flagged_discrepancies",
            type: "string",
          },
          { field: "receipt_summary", as: "receipt_summary", type: "string" },
        ],
      ),

      // 4. Running total computation (runCode)
      n("4", "runCode", "Compute running total & category breakdown", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate running totals and category breakdown from validated items
const items = JSON.parse(vars.validated_items || "[]");
let grandTotal = 0;
const categories = {};
for (const item of items) {
  const amt = parseFloat(item.amount) || 0;
  grandTotal += amt;
  const cat = item.category || "other";
  categories[cat] = (categories[cat] || 0) + amt;
}
const categoryBreakdown = Object.entries(categories)
  .map(([k, v]) => k + ": $" + Number(v).toFixed(2))
  .join(", ");
const receiptRate = parseFloat(vars.receipt_match_rate) || 0;
const missingData = parseInt(vars.items_missing_data) || 0;
const isComplete = receiptRate >= 80 && missingData === 0;
return {
  grand_total: grandTotal.toFixed(2),
  category_breakdown: categoryBreakdown,
  line_item_count: items.length,
  is_complete: isComplete ? "yes" : "no",
  completeness_reason: isComplete ? "All items validated with receipts" : "Missing receipts or incomplete data on " + missingData + " items"
};`,
          outputVariables: [
            { name: "grand_total", type: "string" },
            { name: "category_breakdown", type: "string" },
            { name: "line_item_count", type: "number" },
            { name: "is_complete", type: "string" },
            { name: "completeness_reason", type: "string" },
          ],
        },
      }),

      // 5. Completeness validation condition
      n("5", "condition", "Report complete & validated?", 1000, {
        conditionData: [
          {
            variable: "is_complete",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Formal report creation (AI)
      aiNode(
        "6",
        "AI: Generate formal expense report",
        1240,
        "You are a corporate expense reporting specialist. Generate a professional, formatted expense report ready for manager approval. Include a header with employee name, report title, date range, itemized table, category subtotals, and grand total. Respond JSON: {formatted_report: string, approval_recommendation: string, policy_notes: string}",
        "Employee: {employee_name}. Report: {report_title}. Items: {validated_items}. Grand Total: ${grand_total}. Breakdown: {category_breakdown}. Receipt summary: {receipt_summary}.",
        [
          { field: "formatted_report", as: "formatted_report", type: "string" },
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
          { field: "policy_notes", as: "policy_notes", type: "string" },
        ],
      ),

      // 7. Submission confirmation email
      emailNode(
        "7",
        "Send submission confirmation",
        1480,
        "{employee_email}",
        "Expense Report Submitted: {report_title} - ${grand_total}",
        "Hi {employee_name},\\n\\nYour expense report has been submitted successfully.\\n\\nReport: {report_title}\\nTotal: ${grand_total}\\nItems: {line_item_count}\\nBreakdown: {category_breakdown}\\n\\nReceipt Validation: {receipt_summary}\\n\\n{policy_notes}\\n\\nYour report is now pending manager approval. You will be notified once a decision is made.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // complete -> formal report
      e("5", "7", "output_2"), // incomplete -> still sends confirmation with issues noted
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 624. FAILED PAYMENT RECOVERY BOT (8 nodes)
  // ===================================================================

  "Failed Payment Recovery Bot": {
    summary:
      "Automated recovery flow for failed Stripe payments. A Stripe payment_failed webhook triggers decline reason analysis via runCode, routes on customer messaging type via condition, sends a failure email with payment update link, schedules a smart retry delay, escalates via SMS, checks service pause/retention via condition, and logs recovery outcomes to Sheets.",
    nodes: [
      trigger("Webhook: Stripe payment_failed", "webhook_app", {
        source: "Stripe",
        event: "invoice.payment_failed",
        howItStarts:
          "Stripe fires a webhook when a subscription or invoice payment attempt fails, initiating the recovery sequence",
        setup:
          "Configure Stripe webhook for invoice.payment_failed events. Ensure customer metadata includes email, phone, and plan details.",
        inputFields: [],
      }),

      // 2. Decline reason analysis (runCode)
      n("2", "runCode", "Analyze decline reason & retry strategy", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Stripe payment_failed webhook and determine retry strategy
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = data.customer_email || (data.customer_details || {}).email || "";
const customer_phone = data.customer_phone || (data.customer_details || {}).phone || "";
const customer_name = data.customer_name || (data.customer_details || {}).name || "Customer";
const invoice_id = data.id || data.invoice_id || "";
const amount = ((data.amount_due || data.amount || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const attempt = data.attempt_count || data.next_payment_attempt ? 2 : 1;
const charge = data.charge || {};
const decline_code = typeof charge === "object" ? (charge.failure_code || "unknown") : "unknown";
const decline_message = typeof charge === "object" ? (charge.failure_message || "Payment declined") : "Payment declined";
// Determine retry strategy based on decline code
const hardDeclines = ["stolen_card", "lost_card", "fraudulent", "pickup_card"];
const softDeclines = ["insufficient_funds", "processing_error", "generic_decline"];
const isHard = hardDeclines.includes(decline_code);
const retryHours = isHard ? 0 : attempt <= 1 ? 24 : attempt <= 2 ? 72 : 168;
const messageTone = isHard ? "card_update_required" : "soft_retry";
return {
  customer_email: customer_email,
  customer_phone: customer_phone,
  customer_name: customer_name,
  invoice_id: invoice_id,
  amount_due: amount,
  currency: currency,
  attempt_count: attempt,
  decline_code: decline_code,
  decline_message: decline_message,
  is_hard_decline: isHard ? "yes" : "no",
  message_tone: messageTone,
  retry_delay_hours: retryHours,
  recovery_urgency: attempt >= 3 ? "critical" : isHard ? "high" : "medium"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "invoice_id", type: "string" },
            { name: "amount_due", type: "string" },
            { name: "currency", type: "string" },
            { name: "attempt_count", type: "number" },
            { name: "decline_code", type: "string" },
            { name: "decline_message", type: "string" },
            { name: "is_hard_decline", type: "string" },
            { name: "message_tone", type: "string" },
            { name: "retry_delay_hours", type: "number" },
            { name: "recovery_urgency", type: "string" },
          ],
        },
      }),

      // 3. Customer message routing condition
      n("3", "condition", "Hard decline requiring card update?", 520, {
        conditionData: [
          {
            variable: "is_hard_decline",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Failure email with update link
      emailNode(
        "4",
        "Send payment failure email",
        760,
        "{customer_email}",
        "Action Required: Payment of {currency} {amount_due} Failed",
        "Hi {customer_name},\\n\\nWe were unable to process your payment of {currency} {amount_due} (Invoice: {invoice_id}).\\n\\nReason: {decline_message}\\n\\nPlease update your payment method to avoid service interruption:\\nhttps://billing.example.com/update-payment?invoice={invoice_id}\\n\\nIf you believe this is an error, please contact our support team.",
      ),

      // 5. Smart retry scheduling (delay)
      n("5", "delayAction", "Wait for smart retry window", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "{retry_delay_hours}",
          delayUnit: "hours",
        },
      }),

      // 6. Escalation SMS
      smsNode(
        "6",
        "Send recovery SMS",
        1240,
        "{customer_phone}",
        "Hi {customer_name}, your payment of {currency} {amount_due} failed (attempt {attempt_count}). Update your payment method at https://billing.example.com/update-payment?invoice={invoice_id} to avoid service disruption.",
      ),

      // 7. Service pause / retention condition
      n("7", "condition", "Critical: pause service?", 1480, {
        conditionData: [
          {
            variable: "recovery_urgency",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Recovery logging (Sheets)
      sheetsNode(
        "8",
        "Log recovery outcome (Sheets)",
        1720,
        "PaymentRecovery",
        '[["{invoice_id}","{customer_email}","{amount_due}","{currency}","{decline_code}","{attempt_count}","{recovery_urgency}","{is_hard_decline}","pending"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // hard decline -> immediate email
      e("3", "5", "output_2"), // soft decline -> delay then retry
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // critical -> log with pause flag
      e("7", "8", "output_2"), // not critical -> log as monitoring
    ],
  },

  // ===================================================================
  // 625. FINANCIAL COMPLIANCE CHECK BOT (8 nodes)
  // ===================================================================

  "Financial Compliance Check Bot": {
    summary:
      "Validates financial transactions against compliance policies and routes for approval. A compliance query trigger starts the flow, policy KB is searched, AI validates the transaction, risk level is assessed via condition, medium/high-risk items route for approval, documentation is recorded to Sheets, monthly compliance summaries are emailed, and training gaps are flagged in Slack.",
    nodes: [
      trigger("Webhook: Compliance check requested", "webhook_app", {
        source: "Finance System / Slack",
        event: "compliance.check.requested",
        howItStarts:
          "A compliance review is triggered when a transaction exceeds thresholds, a manual query is submitted, or a scheduled audit fires",
        setup:
          "Configure finance system webhook for transactions exceeding compliance thresholds. Include transaction details, requestor info, and policy references.",
        inputFields: [],
      }),

      // 2. Policy KB search
      aiNode(
        "2",
        "AI: Search compliance policy KB",
        280,
        "You are a financial compliance knowledge base assistant. Given a transaction or compliance query, extract the key details and identify which compliance policies, regulations, and thresholds are relevant. Look for the transaction amount, type, counterparty, jurisdiction, and purpose regardless of payload format. Respond JSON: {transaction_id: string, requestor_email: string, transaction_amount: number, transaction_type: string, counterparty: string, jurisdiction: string, applicable_policies: string, threshold_exceeded: boolean, policy_references: string}",
        "Compliance query payload: {webhook_payload}",
        [
          { field: "transaction_id", as: "transaction_id", type: "string" },
          { field: "requestor_email", as: "requestor_email", type: "string" },
          {
            field: "transaction_amount",
            as: "transaction_amount",
            type: "number",
          },
          { field: "transaction_type", as: "transaction_type", type: "string" },
          { field: "counterparty", as: "counterparty", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          {
            field: "applicable_policies",
            as: "applicable_policies",
            type: "string",
          },
          {
            field: "threshold_exceeded",
            as: "threshold_exceeded",
            type: "string",
          },
          {
            field: "policy_references",
            as: "policy_references",
            type: "string",
          },
        ],
      ),

      // 3. AI transaction validation
      aiNode(
        "3",
        "AI: Validate transaction compliance",
        520,
        "You are a financial compliance officer. Given transaction details and applicable policies, perform a thorough compliance validation. Check AML thresholds, KYC requirements, sanctions screening, internal policy limits, and regulatory requirements. Assign a risk level: low, medium, or high. Respond JSON: {risk_level: string, compliance_status: string, findings: string, required_actions: string, aml_flag: boolean, sanctions_flag: boolean, training_gap: string}",
        "Transaction: {transaction_type} for ${transaction_amount} with {counterparty} in {jurisdiction}. Applicable policies: {applicable_policies}. References: {policy_references}.",
        [
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "compliance_status",
            as: "compliance_status",
            type: "string",
          },
          { field: "findings", as: "compliance_findings", type: "string" },
          { field: "required_actions", as: "required_actions", type: "string" },
          { field: "aml_flag", as: "aml_flag", type: "string" },
          { field: "sanctions_flag", as: "sanctions_flag", type: "string" },
          { field: "training_gap", as: "training_gap", type: "string" },
        ],
      ),

      // 4. Risk level condition
      n("4", "condition", "Medium or high risk?", 760, {
        conditionData: [
          {
            variable: "risk_level",
            value: "low",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Approval routing for medium/high
      emailNode(
        "5",
        "Route for compliance approval",
        1000,
        "{compliance_officer_email}",
        "Compliance Review Required: {risk_level} Risk - {transaction_type} ${transaction_amount}",
        "Compliance Review Needed\\n\\nTransaction ID: {transaction_id}\\nType: {transaction_type}\\nAmount: ${transaction_amount}\\nCounterparty: {counterparty}\\nJurisdiction: {jurisdiction}\\nRisk Level: {risk_level}\\n\\nFindings:\\n{compliance_findings}\\n\\nRequired Actions:\\n{required_actions}\\n\\nAML Flag: {aml_flag} | Sanctions Flag: {sanctions_flag}\\n\\nPlease review and approve/reject within 24 hours.",
      ),

      // 6. Documentation recording (Sheets)
      sheetsNode(
        "6",
        "Log compliance record (Sheets)",
        1240,
        "ComplianceLog",
        '[["{transaction_id}","{transaction_type}","{transaction_amount}","{counterparty}","{jurisdiction}","{risk_level}","{compliance_status}","{compliance_findings}","{aml_flag}","{sanctions_flag}"]]',
      ),

      // 7. Monthly compliance summary email
      emailNode(
        "7",
        "Send compliance summary",
        1480,
        "{requestor_email}",
        "Compliance Check Complete: {transaction_id} - {compliance_status}",
        "Compliance Check Results\\n\\nTransaction: {transaction_id}\\nStatus: {compliance_status}\\nRisk Level: {risk_level}\\n\\nFindings: {compliance_findings}\\n\\nRequired Actions: {required_actions}\\n\\nPolicy References: {policy_references}\\n\\nThis record has been logged for audit purposes.",
      ),

      // 8. Training gap flagging (Slack)
      slackNode(
        "8",
        "Slack: Flag training gaps",
        1720,
        ":warning: Compliance Training Gap Detected\\nTransaction: {transaction_id} ({transaction_type} - ${transaction_amount})\\nRisk: {risk_level}\\nTraining Gap: {training_gap}\\nAction: Schedule refresher training for relevant team members.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // medium/high risk -> approval routing
      e("4", "6", "output_2"), // low risk -> straight to documentation
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 626. FINANCIAL DATA ROOM ORGANIZER (8 nodes)
  // ===================================================================

  "Financial Data Room Organizer": {
    summary:
      "Automatically classifies, indexes, and organizes documents uploaded to a financial data room. An upload trigger starts the flow, AI classifies the document, metadata is extracted via runCode, duplicates/versions are detected via condition, documents are indexed with standardized naming, checklist gap analysis runs, the data room manager is notified via email, and a completeness report is logged to Sheets.",
    nodes: [
      trigger("Webhook: Document uploaded to data room", "webhook_app", {
        source: "Data Room / Cloud Storage",
        event: "document.uploaded",
        howItStarts:
          "A new document is uploaded to the financial data room, triggering automatic classification and organization",
        setup:
          "Configure data room or cloud storage webhook (Box, Dropbox, Google Drive) to fire on file upload. Include file metadata, uploader info, and folder path.",
        inputFields: [],
      }),

      // 2. AI classification
      aiNode(
        "2",
        "AI: Classify document type",
        280,
        "You are a financial document classification expert specializing in M&A data rooms, fundraising, and due diligence. Given file metadata, classify the document into a standard data room category: financial_statements, tax_returns, contracts, ip_documents, corporate_governance, employment, insurance, regulatory, cap_table, projections, legal, real_estate, customer_data, or other. Also extract the document period and entity. Respond JSON: {file_name: string, uploader_email: string, category: string, subcategory: string, document_period: string, entity_name: string, confidentiality_level: string, standard_name: string, original_path: string}",
        "Document upload payload: {webhook_payload}",
        [
          { field: "file_name", as: "file_name", type: "string" },
          { field: "uploader_email", as: "uploader_email", type: "string" },
          { field: "category", as: "doc_category", type: "string" },
          { field: "subcategory", as: "doc_subcategory", type: "string" },
          { field: "document_period", as: "document_period", type: "string" },
          { field: "entity_name", as: "entity_name", type: "string" },
          {
            field: "confidentiality_level",
            as: "confidentiality_level",
            type: "string",
          },
          { field: "standard_name", as: "standard_name", type: "string" },
          { field: "original_path", as: "original_path", type: "string" },
        ],
      ),

      // 3. Metadata extraction (runCode)
      n("3", "runCode", "Extract metadata & compute hashes", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract document metadata and generate deduplication hash
const fileName = vars.file_name || "unknown";
const category = vars.doc_category || "other";
const period = vars.document_period || "undated";
const entity = vars.entity_name || "unknown";
const standardName = vars.standard_name || fileName;
// Generate a simple dedup key from category + period + entity + name pattern
const dedupKey = (category + "_" + period + "_" + entity + "_" + standardName.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()).substring(0, 100);
const fileExt = fileName.split(".").pop() || "pdf";
const indexPath = category + "/" + entity + "/" + period + "/" + standardName + "." + fileExt;
const uploadTimestamp = new Date().toISOString();
return {
  dedup_key: dedupKey,
  index_path: indexPath,
  file_extension: fileExt,
  upload_timestamp: uploadTimestamp,
  is_potential_duplicate: "pending_check",
  standardized_filename: standardName + "." + fileExt
};`,
          outputVariables: [
            { name: "dedup_key", type: "string" },
            { name: "index_path", type: "string" },
            { name: "file_extension", type: "string" },
            { name: "upload_timestamp", type: "string" },
            { name: "is_potential_duplicate", type: "string" },
            { name: "standardized_filename", type: "string" },
          ],
        },
      }),

      // 4. Duplicate / version detection condition
      n("4", "condition", "Potential duplicate detected?", 760, {
        conditionData: [
          {
            variable: "is_potential_duplicate",
            value: "pending_check",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Index + standardized naming (AI)
      aiNode(
        "5",
        "AI: Index & apply standard naming",
        1000,
        "You are a data room indexing specialist. Given the document metadata, create a proper index entry with standardized naming following data room conventions. Check category completeness against a standard due diligence checklist (financial_statements, tax_returns, contracts, ip_documents, corporate_governance, employment, insurance, regulatory, cap_table, projections, legal). Respond JSON: {index_entry: string, naming_applied: string, checklist_category_status: string, missing_in_category: string, category_completion_pct: number}",
        "File: {standardized_filename}. Category: {doc_category}/{doc_subcategory}. Entity: {entity_name}. Period: {document_period}. Index path: {index_path}.",
        [
          { field: "index_entry", as: "index_entry", type: "string" },
          { field: "naming_applied", as: "naming_applied", type: "string" },
          {
            field: "checklist_category_status",
            as: "checklist_category_status",
            type: "string",
          },
          {
            field: "missing_in_category",
            as: "missing_in_category",
            type: "string",
          },
          {
            field: "category_completion_pct",
            as: "category_completion_pct",
            type: "number",
          },
        ],
      ),

      // 6. Checklist gap analysis (AI)
      aiNode(
        "6",
        "AI: Data room completeness analysis",
        1240,
        "You are a due diligence checklist analyst. Given the current category completion status, identify the most critical missing documents and provide a prioritized action list for the data room manager. Respond JSON: {overall_completeness: string, critical_gaps: string, priority_uploads_needed: string, estimated_completion_date: string}",
        "Category status: {checklist_category_status}. Missing in {doc_category}: {missing_in_category}. Category completion: {category_completion_pct}%.",
        [
          {
            field: "overall_completeness",
            as: "overall_completeness",
            type: "string",
          },
          { field: "critical_gaps", as: "critical_gaps", type: "string" },
          {
            field: "priority_uploads_needed",
            as: "priority_uploads_needed",
            type: "string",
          },
          {
            field: "estimated_completion_date",
            as: "estimated_completion_date",
            type: "string",
          },
        ],
      ),

      // 7. Data room manager notification email
      emailNode(
        "7",
        "Notify data room manager",
        1480,
        "{data_room_manager_email}",
        "Data Room Update: {standardized_filename} classified as {doc_category}",
        "Data Room Document Processed\\n\\nFile: {standardized_filename}\\nCategory: {doc_category} / {doc_subcategory}\\nEntity: {entity_name}\\nPeriod: {document_period}\\nConfidentiality: {confidentiality_level}\\nIndex Path: {index_path}\\n\\nCategory Completion: {category_completion_pct}%\\nCritical Gaps: {critical_gaps}\\n\\nPriority Uploads Needed: {priority_uploads_needed}\\n\\nUploaded by: {uploader_email}",
      ),

      // 8. Completeness report (Sheets)
      sheetsNode(
        "8",
        "Log completeness report (Sheets)",
        1720,
        "DataRoomIndex",
        '[["{upload_timestamp}","{standardized_filename}","{doc_category}","{doc_subcategory}","{entity_name}","{document_period}","{confidentiality_level}","{index_path}","{category_completion_pct}","{overall_completeness}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // potential duplicate -> index with version handling
      e("4", "5", "output_2"), // new document -> index normally
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 627. FINANCIAL FORECAST GENERATOR (8 nodes)
  // ===================================================================

  "Financial Forecast Generator": {
    summary:
      "Generates conservative/base/optimistic financial forecasts from historical data. A schedule or manual request triggers historical revenue pull from Stripe, pipeline and bookings data is pulled, expense and cost data is pulled, three-scenario modeling runs via runCode, AI performs sensitivity analysis, a forecast workbook is written to Sheets, and the forecast is distributed via email with risk notes.",
    nodes: [
      trigger("Schedule: Monthly forecast generation", "schedule", {
        schedule: "0 8 1 * *",
        howItStarts:
          "Runs on the first of each month at 8 AM, or triggered manually when an ad-hoc forecast is requested",
        setup:
          "Configure Stripe API for revenue history. Set up CRM API for pipeline data. Prepare expense tracking integration. Create Google Sheets template for forecast output.",
        inputFields: [],
      }),

      // 2. Historical revenue pull (Stripe)
      stripeNode(
        "2",
        "Pull historical revenue (Stripe)",
        280,
        "GET",
        "charges?limit=100&created[gte]={six_months_ago_unix}",
        [],
        [
          { name: "data", customName: "revenue_data", type: "string" },
          { name: "has_more", customName: "has_more_charges", type: "string" },
        ],
      ),

      // 3. Pipeline + bookings pull
      hubspotNode(
        "3",
        "Pull pipeline & bookings (HubSpot)",
        520,
        "GET",
        "crm/v3/objects/deals?properties=amount,dealstage,closedate,pipeline&limit=100",
        [],
        [
          { name: "results", customName: "pipeline_data", type: "string" },
          { name: "total", customName: "total_deals", type: "number" },
        ],
      ),

      // 4. Expense + costs pull
      sheetsNode(
        "4",
        "Pull expense & cost data (Sheets)",
        760,
        "Expenses",
        "[[]]",
      ),

      // 5. Conservative/base/optimistic modeling (runCode)
      n("5", "runCode", "Build 3-scenario forecast model", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build conservative, base, and optimistic forecast scenarios
const revenue = JSON.parse(vars.revenue_data || "[]");
const pipeline = JSON.parse(vars.pipeline_data || "[]");
// Calculate monthly revenue trend
const monthlyRevenue = {};
for (const charge of revenue) {
  if (!charge.created) continue;
  const d = new Date(charge.created * 1000);
  const key = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
  monthlyRevenue[key] = (monthlyRevenue[key] || 0) + ((charge.amount || 0) / 100);
}
const months = Object.keys(monthlyRevenue).sort();
const revenueValues = months.map(m => monthlyRevenue[m]);
const avgMonthly = revenueValues.length > 0 ? revenueValues.reduce((a, b) => a + b, 0) / revenueValues.length : 0;
const lastMonth = revenueValues.length > 0 ? revenueValues[revenueValues.length - 1] : 0;
const growthRate = revenueValues.length >= 2 ? (revenueValues[revenueValues.length - 1] - revenueValues[0]) / (revenueValues[0] || 1) / revenueValues.length : 0;
// Pipeline weighted value
let pipelineWeighted = 0;
for (const deal of pipeline) {
  const amt = parseFloat(deal.properties ? deal.properties.amount : deal.amount) || 0;
  const stage = deal.properties ? deal.properties.dealstage : deal.dealstage || "";
  const weight = stage === "closedwon" ? 1 : stage === "contractsent" ? 0.8 : stage === "proposalmade" ? 0.4 : 0.1;
  pipelineWeighted += amt * weight;
}
// 6-month forecasts
const forecast = (rate) => {
  const f = [];
  let base = lastMonth;
  for (let i = 1; i <= 6; i++) { base = base * (1 + rate); f.push(Math.round(base)); }
  return f;
};
const conservative = forecast(growthRate * 0.5 - 0.02);
const baseForecast = forecast(growthRate);
const optimistic = forecast(growthRate * 1.5 + 0.02);
return {
  avg_monthly_revenue: avgMonthly.toFixed(2),
  last_month_revenue: lastMonth.toFixed(2),
  monthly_growth_rate: (growthRate * 100).toFixed(1),
  pipeline_weighted_value: pipelineWeighted.toFixed(2),
  conservative_forecast: JSON.stringify(conservative),
  base_forecast: JSON.stringify(baseForecast),
  optimistic_forecast: JSON.stringify(optimistic),
  conservative_6mo_total: conservative.reduce((a, b) => a + b, 0).toFixed(2),
  base_6mo_total: baseForecast.reduce((a, b) => a + b, 0).toFixed(2),
  optimistic_6mo_total: optimistic.reduce((a, b) => a + b, 0).toFixed(2),
  months_analyzed: months.length
};`,
          outputVariables: [
            { name: "avg_monthly_revenue", type: "string" },
            { name: "last_month_revenue", type: "string" },
            { name: "monthly_growth_rate", type: "string" },
            { name: "pipeline_weighted_value", type: "string" },
            { name: "conservative_forecast", type: "string" },
            { name: "base_forecast", type: "string" },
            { name: "optimistic_forecast", type: "string" },
            { name: "conservative_6mo_total", type: "string" },
            { name: "base_6mo_total", type: "string" },
            { name: "optimistic_6mo_total", type: "string" },
            { name: "months_analyzed", type: "number" },
          ],
        },
      }),

      // 6. AI sensitivity analysis
      aiNode(
        "6",
        "AI: Sensitivity & risk analysis",
        1240,
        "You are a financial planning and analysis (FP&A) expert. Given three forecast scenarios (conservative, base, optimistic) and their underlying assumptions, perform a sensitivity analysis. Identify the top 3 risk factors that could move actuals below the conservative case and the top 3 tailwinds that could push above optimistic. Respond JSON: {sensitivity_summary: string, key_risks: string, key_tailwinds: string, recommended_scenario: string, confidence_level: string, executive_commentary: string}",
        "Historical avg monthly: ${avg_monthly_revenue}. Last month: ${last_month_revenue}. Growth rate: {monthly_growth_rate}%. Pipeline weighted: ${pipeline_weighted_value}. 6mo totals - Conservative: ${conservative_6mo_total}, Base: ${base_6mo_total}, Optimistic: ${optimistic_6mo_total}. Months analyzed: {months_analyzed}.",
        [
          {
            field: "sensitivity_summary",
            as: "sensitivity_summary",
            type: "string",
          },
          { field: "key_risks", as: "key_risks", type: "string" },
          { field: "key_tailwinds", as: "key_tailwinds", type: "string" },
          {
            field: "recommended_scenario",
            as: "recommended_scenario",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "executive_commentary",
            as: "executive_commentary",
            type: "string",
          },
        ],
      ),

      // 7. Forecast workbook (Sheets)
      sheetsNode(
        "7",
        "Write forecast workbook (Sheets)",
        1480,
        "Forecast",
        '[["{last_month_revenue}","{monthly_growth_rate}","{pipeline_weighted_value}","{conservative_6mo_total}","{base_6mo_total}","{optimistic_6mo_total}","{recommended_scenario}","{confidence_level}","{key_risks}","{key_tailwinds}"]]',
      ),

      // 8. Distribution with risks (email)
      emailNode(
        "8",
        "Distribute forecast to leadership",
        1720,
        "{finance_team_email}",
        "Monthly Financial Forecast: {recommended_scenario} Scenario Recommended",
        "Financial Forecast Report\\n\\nExecutive Commentary:\\n{executive_commentary}\\n\\n6-Month Forecast Summary:\\n- Conservative: ${conservative_6mo_total}\\n- Base: ${base_6mo_total}\\n- Optimistic: ${optimistic_6mo_total}\\n\\nRecommended Scenario: {recommended_scenario} (Confidence: {confidence_level})\\n\\nSensitivity Analysis:\\n{sensitivity_summary}\\n\\nKey Risks:\\n{key_risks}\\n\\nKey Tailwinds:\\n{key_tailwinds}\\n\\nFull workbook available in the shared Forecast Google Sheet.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 628. FINANCIAL Q&A BOT (7 nodes)
  // ===================================================================

  "Financial Q&A Bot": {
    summary:
      "AI-powered financial Q&A bot that classifies questions, retrieves relevant data or policy, generates grounded answers, and flags low-confidence responses for human verification. A Slack/portal trigger starts the flow, AI classifies the question, data/policy/calculation is retrieved, AI generates a grounded answer, confidence is checked via condition, low-confidence items route for finance verification, and all Q&A is logged to Sheets.",
    nodes: [
      trigger("Webhook: Financial question submitted", "webhook_app", {
        source: "Slack / Finance Portal",
        event: "finance.question.submitted",
        howItStarts:
          "An employee submits a financial question via Slack command or the internal finance portal",
        setup:
          "Configure Slack slash command (/finance-ask) or portal form webhook. Payload includes the question text, asker identity, and optional context.",
        inputFields: [],
      }),

      // 2. AI question classification
      aiNode(
        "2",
        "AI: Classify financial question",
        280,
        "You are a financial question triage specialist. Given a question, classify it into one of these categories: data_lookup (requires pulling specific numbers), policy_inquiry (requires referencing company financial policies), calculation (requires computing a value), strategic (requires analysis/opinion), or general (basic info). Also identify the data sources needed. Respond JSON: {asker_email: string, asker_name: string, question_text: string, category: string, data_sources_needed: string, urgency: string, complexity: string}",
        "Financial question payload: {webhook_payload}",
        [
          { field: "asker_email", as: "asker_email", type: "string" },
          { field: "asker_name", as: "asker_name", type: "string" },
          { field: "question_text", as: "question_text", type: "string" },
          { field: "category", as: "question_category", type: "string" },
          {
            field: "data_sources_needed",
            as: "data_sources_needed",
            type: "string",
          },
          { field: "urgency", as: "question_urgency", type: "string" },
          { field: "complexity", as: "question_complexity", type: "string" },
        ],
      ),

      // 3. Data/policy/calculation retrieval
      aiNode(
        "3",
        "AI: Retrieve relevant data & policy",
        520,
        "You are a financial knowledge retrieval system. Given the classified question and required data sources, retrieve or synthesize the relevant financial data, policy excerpts, or calculation inputs needed to answer the question. Be specific and cite sources. Respond JSON: {retrieved_data: string, policy_excerpts: string, calculation_inputs: string, source_citations: string, data_freshness: string}",
        "Question: {question_text}. Category: {question_category}. Data sources needed: {data_sources_needed}.",
        [
          { field: "retrieved_data", as: "retrieved_data", type: "string" },
          { field: "policy_excerpts", as: "policy_excerpts", type: "string" },
          {
            field: "calculation_inputs",
            as: "calculation_inputs",
            type: "string",
          },
          { field: "source_citations", as: "source_citations", type: "string" },
          { field: "data_freshness", as: "data_freshness", type: "string" },
        ],
      ),

      // 4. AI grounded answer
      aiNode(
        "4",
        "AI: Generate grounded answer",
        760,
        "You are a financial analyst providing accurate, grounded answers. Given the question, retrieved data, and policy excerpts, generate a clear, professional answer. Rate your confidence from 0-100 based on data availability and certainty. If confidence is below 70, explicitly note which parts are uncertain. Respond JSON: {answer: string, confidence_score: number, caveats: string, follow_up_suggestion: string, needs_human_review: boolean}",
        "Question: {question_text}. Retrieved data: {retrieved_data}. Policy: {policy_excerpts}. Calculations: {calculation_inputs}. Sources: {source_citations}.",
        [
          { field: "answer", as: "generated_answer", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          { field: "caveats", as: "answer_caveats", type: "string" },
          {
            field: "follow_up_suggestion",
            as: "follow_up_suggestion",
            type: "string",
          },
          {
            field: "needs_human_review",
            as: "needs_human_review",
            type: "string",
          },
        ],
      ),

      // 5. Low-confidence condition
      n("5", "condition", "Confidence below 70%?", 1000, {
        conditionData: [
          {
            variable: "confidence_score",
            value: "70",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Finance verification recommendation
      emailNode(
        "6",
        "Request finance team verification",
        1240,
        "{finance_team_email}",
        "Low-Confidence Q&A Response Needs Review - {question_category}",
        "A financial question received a low-confidence automated response and needs human verification.\\n\\nAsker: {asker_name} ({asker_email})\\nQuestion: {question_text}\\nCategory: {question_category}\\n\\nAI-Generated Answer:\\n{generated_answer}\\n\\nConfidence: {confidence_score}%\\nCaveats: {answer_caveats}\\n\\nSources Used: {source_citations}\\nData Freshness: {data_freshness}\\n\\nPlease review and provide the verified response to the asker.",
      ),

      // 7. Q&A logging (Sheets)
      sheetsNode(
        "7",
        "Log Q&A record (Sheets)",
        1480,
        "FinanceQA",
        '[["{asker_email}","{question_text}","{question_category}","{generated_answer}","{confidence_score}","{answer_caveats}","{source_citations}","{needs_human_review}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // low confidence -> finance team review
      e("5", "7", "output_2"), // high confidence -> log directly
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 629. FINANCIAL REPORT GENERATOR (8 nodes)
  // ===================================================================

  "Financial Report Generator": {
    summary:
      "Generates a weekly financial report with KPIs, executive commentary, and distribution. A weekly schedule triggers revenue/expense/cash data pull, KPIs are calculated via runCode, AI generates executive commentary, a structured report is written to Sheets, leadership receives the email, a Slack summary is posted, and the report is archived.",
    nodes: [
      trigger("Schedule: Weekly financial report", "schedule", {
        schedule: "0 7 * * 1",
        howItStarts:
          "Runs every Monday at 7 AM to generate the weekly financial report covering the prior week",
        setup:
          "Configure Stripe API for revenue data, expense tracking integration, and bank API for cash position. Set up Google Sheets report template and distribution list.",
        inputFields: [],
      }),

      // 2. Revenue/expense/cash pull
      stripeNode(
        "2",
        "Pull weekly revenue data (Stripe)",
        280,
        "GET",
        "balance/history?limit=100&created[gte]={one_week_ago_unix}",
        [],
        [
          { name: "data", customName: "balance_transactions", type: "string" },
          { name: "has_more", customName: "has_more_txns", type: "string" },
        ],
      ),

      // 3. KPI calculation (runCode)
      n("3", "runCode", "Calculate weekly KPIs", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate key financial KPIs from balance transactions
const txns = JSON.parse(vars.balance_transactions || "[]");
let totalRevenue = 0, totalFees = 0, refunds = 0, txnCount = 0;
for (const txn of txns) {
  const amt = (txn.amount || 0) / 100;
  const fee = (txn.fee || 0) / 100;
  if (txn.type === "charge" || txn.type === "payment") {
    totalRevenue += amt;
    totalFees += fee;
    txnCount++;
  } else if (txn.type === "refund" || txn.type === "adjustment") {
    refunds += Math.abs(amt);
  }
}
const netRevenue = totalRevenue - refunds;
const avgTxnSize = txnCount > 0 ? (totalRevenue / txnCount) : 0;
const feeRate = totalRevenue > 0 ? ((totalFees / totalRevenue) * 100) : 0;
const refundRate = totalRevenue > 0 ? ((refunds / totalRevenue) * 100) : 0;
return {
  gross_revenue: totalRevenue.toFixed(2),
  net_revenue: netRevenue.toFixed(2),
  total_fees: totalFees.toFixed(2),
  refund_total: refunds.toFixed(2),
  transaction_count: txnCount,
  avg_transaction_size: avgTxnSize.toFixed(2),
  fee_rate_pct: feeRate.toFixed(1),
  refund_rate_pct: refundRate.toFixed(1),
  week_summary: "Gross: $" + totalRevenue.toFixed(2) + " | Net: $" + netRevenue.toFixed(2) + " | " + txnCount + " transactions"
};`,
          outputVariables: [
            { name: "gross_revenue", type: "string" },
            { name: "net_revenue", type: "string" },
            { name: "total_fees", type: "string" },
            { name: "refund_total", type: "string" },
            { name: "transaction_count", type: "number" },
            { name: "avg_transaction_size", type: "string" },
            { name: "fee_rate_pct", type: "string" },
            { name: "refund_rate_pct", type: "string" },
            { name: "week_summary", type: "string" },
          ],
        },
      }),

      // 4. AI executive commentary
      aiNode(
        "4",
        "AI: Generate executive commentary",
        760,
        "You are a CFO-level financial analyst. Given weekly financial KPIs, generate a concise executive commentary highlighting performance trends, concerns, and recommendations. Focus on actionable insights. Respond JSON: {executive_summary: string, key_highlights: string, concerns: string, recommendations: string, week_over_week_assessment: string}",
        "Weekly KPIs - Gross Revenue: ${gross_revenue}, Net Revenue: ${net_revenue}, Transactions: {transaction_count}, Avg Size: ${avg_transaction_size}, Fees: {fee_rate_pct}%, Refund Rate: {refund_rate_pct}%. Summary: {week_summary}.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_highlights", as: "key_highlights", type: "string" },
          { field: "concerns", as: "financial_concerns", type: "string" },
          {
            field: "recommendations",
            as: "financial_recommendations",
            type: "string",
          },
          {
            field: "week_over_week_assessment",
            as: "wow_assessment",
            type: "string",
          },
        ],
      ),

      // 5. Structured report (Sheets)
      sheetsNode(
        "5",
        "Write weekly report (Sheets)",
        1000,
        "WeeklyFinance",
        '[["{gross_revenue}","{net_revenue}","{total_fees}","{refund_total}","{transaction_count}","{avg_transaction_size}","{fee_rate_pct}","{refund_rate_pct}","{executive_summary}","{financial_recommendations}"]]',
      ),

      // 6. Leadership email
      emailNode(
        "6",
        "Send report to leadership",
        1240,
        "{leadership_email}",
        "Weekly Financial Report: ${net_revenue} Net Revenue",
        "Weekly Financial Report\\n\\nExecutive Summary:\\n{executive_summary}\\n\\nKey Metrics:\\n- Gross Revenue: ${gross_revenue}\\n- Net Revenue: ${net_revenue}\\n- Transactions: {transaction_count}\\n- Avg Transaction: ${avg_transaction_size}\\n- Processing Fees: ${total_fees} ({fee_rate_pct}%)\\n- Refunds: ${refund_total} ({refund_rate_pct}%)\\n\\nHighlights: {key_highlights}\\n\\nConcerns: {financial_concerns}\\n\\nRecommendations: {financial_recommendations}\\n\\nWeek-over-Week: {wow_assessment}\\n\\nDetailed data available in the Weekly Finance Google Sheet.",
      ),

      // 7. Slack summary
      slackNode(
        "7",
        "Slack: Weekly finance summary",
        1480,
        ":chart_with_upwards_trend: Weekly Finance Report\\nNet Revenue: ${net_revenue} | Transactions: {transaction_count}\\nAvg Size: ${avg_transaction_size} | Refund Rate: {refund_rate_pct}%\\n{executive_summary}",
        400,
      ),

      // 8. Archive logging
      sheetsNode(
        "8",
        "Archive report log (Sheets)",
        1720,
        "ReportArchive",
        '[["{gross_revenue}","{net_revenue}","{transaction_count}","{refund_rate_pct}","{executive_summary}","weekly","complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 630. GRANT BUDGET TRACKER (8 nodes)
  // ===================================================================

  "Grant Budget Tracker": {
    summary:
      "Tracks grant-tagged expenses against budgets, validates eligibility, and generates compliance reports. A grant-tagged expense trigger starts the flow, grant rules and balance are pulled, eligibility is validated via runCode, allocation is computed, the grant ledger is updated in Sheets, burn pace is checked via condition, an agency compliance report is emailed, and stakeholders are alerted in Slack.",
    nodes: [
      trigger("Webhook: Grant-tagged expense submitted", "webhook_app", {
        source: "Expense System / Accounting",
        event: "expense.grant_tagged",
        howItStarts:
          "An expense is submitted with a grant tag, triggering budget validation and tracking against the specific grant's rules and remaining balance",
        setup:
          "Configure expense system webhook to fire when an expense is tagged to a grant. Include expense details, grant ID, and submitter info.",
        inputFields: [],
      }),

      // 2. Grant rules + balance pull
      aiNode(
        "2",
        "AI: Extract expense & pull grant rules",
        280,
        "You are a grant budget management specialist. Parse the expense submission to extract all relevant details and identify the grant, its budget rules, and allowable expense categories. Respond JSON: {expense_id: string, submitter_email: string, grant_id: string, grant_name: string, expense_amount: number, expense_category: string, expense_description: string, vendor: string, grant_total_budget: number, grant_spent_to_date: number, grant_remaining: number, allowable_categories: string, max_single_expense: number, grant_end_date: string}",
        "Grant expense payload: {webhook_payload}",
        [
          { field: "expense_id", as: "expense_id", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "grant_id", as: "grant_id", type: "string" },
          { field: "grant_name", as: "grant_name", type: "string" },
          { field: "expense_amount", as: "expense_amount", type: "number" },
          { field: "expense_category", as: "expense_category", type: "string" },
          {
            field: "expense_description",
            as: "expense_description",
            type: "string",
          },
          { field: "vendor", as: "vendor", type: "string" },
          {
            field: "grant_total_budget",
            as: "grant_total_budget",
            type: "number",
          },
          {
            field: "grant_spent_to_date",
            as: "grant_spent_to_date",
            type: "number",
          },
          { field: "grant_remaining", as: "grant_remaining", type: "number" },
          {
            field: "allowable_categories",
            as: "allowable_categories",
            type: "string",
          },
          {
            field: "max_single_expense",
            as: "max_single_expense",
            type: "number",
          },
          { field: "grant_end_date", as: "grant_end_date", type: "string" },
        ],
      ),

      // 3. Eligibility validation (runCode)
      n(
        "3",
        "runCode",
        "Validate grant eligibility & compute allocation",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Validate expense against grant rules
const amount = parseFloat(vars.expense_amount) || 0;
const remaining = parseFloat(vars.grant_remaining) || 0;
const maxSingle = parseFloat(vars.max_single_expense) || Infinity;
const allowable = (vars.allowable_categories || "").toLowerCase().split(",").map(c => c.trim());
const category = (vars.expense_category || "").toLowerCase().trim();
const endDate = new Date(vars.grant_end_date || "2099-12-31");
const now = new Date();
const errors = [];
if (amount > remaining) errors.push("Exceeds remaining grant balance ($" + remaining.toFixed(2) + ")");
if (amount > maxSingle) errors.push("Exceeds single expense limit ($" + maxSingle.toFixed(2) + ")");
if (allowable.length > 0 && allowable[0] !== "" && !allowable.includes(category)) errors.push("Category '" + category + "' not allowable under this grant");
if (now > endDate) errors.push("Grant period has ended (" + vars.grant_end_date + ")");
const isEligible = errors.length === 0;
const newSpent = parseFloat(vars.grant_spent_to_date) + amount;
const newRemaining = parseFloat(vars.grant_total_budget) - newSpent;
const burnPct = (newSpent / parseFloat(vars.grant_total_budget) * 100);
const burnPace = burnPct > 90 ? "critical" : burnPct > 75 ? "high" : burnPct > 50 ? "moderate" : "healthy";
return {
  is_eligible: isEligible ? "yes" : "no",
  eligibility_errors: errors.join("; ") || "None",
  new_spent_to_date: newSpent.toFixed(2),
  new_remaining: newRemaining.toFixed(2),
  burn_percentage: burnPct.toFixed(1),
  burn_pace: burnPace,
  allocation_status: isEligible ? "approved" : "rejected"
};`,
            outputVariables: [
              { name: "is_eligible", type: "string" },
              { name: "eligibility_errors", type: "string" },
              { name: "new_spent_to_date", type: "string" },
              { name: "new_remaining", type: "string" },
              { name: "burn_percentage", type: "string" },
              { name: "burn_pace", type: "string" },
              { name: "allocation_status", type: "string" },
            ],
          },
        },
      ),

      // 4. Allocation computation (AI confirmation)
      aiNode(
        "4",
        "AI: Compute allocation & compliance notes",
        760,
        "You are a grant compliance analyst. Given the eligibility determination and budget impact, provide compliance notes, allocation coding, and any agency-specific reporting requirements. Respond JSON: {allocation_code: string, compliance_notes: string, reporting_requirements: string, agency_flags: string}",
        "Grant: {grant_name} ({grant_id}). Expense: ${expense_amount} for {expense_category} - {expense_description}. Eligibility: {allocation_status}. Errors: {eligibility_errors}. Burn: {burn_percentage}% ({burn_pace}). Remaining: ${new_remaining}.",
        [
          { field: "allocation_code", as: "allocation_code", type: "string" },
          { field: "compliance_notes", as: "compliance_notes", type: "string" },
          {
            field: "reporting_requirements",
            as: "reporting_requirements",
            type: "string",
          },
          { field: "agency_flags", as: "agency_flags", type: "string" },
        ],
      ),

      // 5. Grant ledger update (Sheets)
      sheetsNode(
        "5",
        "Update grant ledger (Sheets)",
        1000,
        "GrantLedger",
        '[["{expense_id}","{grant_id}","{grant_name}","{expense_amount}","{expense_category}","{vendor}","{allocation_status}","{allocation_code}","{new_spent_to_date}","{new_remaining}","{burn_percentage}","{burn_pace}"]]',
      ),

      // 6. Burn pace condition
      n("6", "condition", "Burn pace high or critical?", 1240, {
        conditionData: [
          {
            variable: "burn_pace",
            value: "healthy",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "burn_pace",
            value: "moderate",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Agency compliance report (email)
      emailNode(
        "7",
        "Send compliance report",
        1480,
        "{grant_manager_email}",
        "Grant Alert: {grant_name} - {burn_pace} Burn Pace ({burn_percentage}%)",
        "Grant Budget Alert\\n\\nGrant: {grant_name} ({grant_id})\\nStatus: {allocation_status}\\nBurn Pace: {burn_pace} ({burn_percentage}% consumed)\\n\\nLatest Expense:\\n- Amount: ${expense_amount}\\n- Category: {expense_category}\\n- Vendor: {vendor}\\n- Description: {expense_description}\\n\\nBudget Position:\\n- Total Budget: ${grant_total_budget}\\n- Spent to Date: ${new_spent_to_date}\\n- Remaining: ${new_remaining}\\n\\nCompliance Notes: {compliance_notes}\\nAgency Flags: {agency_flags}\\n\\nReporting Requirements: {reporting_requirements}",
      ),

      // 8. Stakeholder Slack alert
      slackNode(
        "8",
        "Slack: Grant budget alert",
        1720,
        ":money_with_wings: Grant Budget Alert\\nGrant: {grant_name} ({grant_id})\\nBurn: {burn_percentage}% ({burn_pace})\\nRemaining: ${new_remaining}\\nLatest: ${expense_amount} for {expense_category}\\nStatus: {allocation_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // high/critical burn -> compliance report
      e("6", "8", "output_2"), // healthy -> just Slack alert
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 631. INSURANCE OR DOCUMENT RENEWAL REMINDER (7 nodes)
  // ===================================================================

  "Insurance or Document Renewal Reminder": {
    summary:
      "Scans a document/insurance registry daily for upcoming expirations and sends tiered reminders. A daily schedule triggers registry scanning, expiry window detection runs via runCode, urgency is assessed via condition (90/30/7/expired), renewal reminder emails are sent, work tasks are created, expired items escalate via SMS/Slack, and the renewal log is updated in Sheets.",
    nodes: [
      trigger("Schedule: Daily expiry scan", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to scan the document/insurance registry for upcoming expirations within 90, 30, 7, and 0-day windows",
        setup:
          "Configure Google Sheets or database with a registry of documents/insurance policies including expiry dates, owner emails, and renewal contacts. Set up SMS provider for urgent escalations.",
        inputFields: [],
      }),

      // 2. Expiry window detection (runCode)
      n("2", "runCode", "Detect expiry windows", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Scan registry and classify items by urgency window
// In production, this would read from the Sheets registry
const now = new Date();
const DAY = 86400000;
// Simulated registry scan results - in production, pulled from Sheets API
const registryItems = JSON.parse(vars.registry_data || "[]");
const results = { expired: [], urgent_7: [], soon_30: [], upcoming_90: [], safe: [] };
for (const item of registryItems) {
  const expiry = new Date(item.expiry_date);
  const daysUntil = Math.floor((expiry.getTime() - now.getTime()) / DAY);
  const entry = { ...item, days_until_expiry: daysUntil };
  if (daysUntil <= 0) results.expired.push(entry);
  else if (daysUntil <= 7) results.urgent_7.push(entry);
  else if (daysUntil <= 30) results.soon_30.push(entry);
  else if (daysUntil <= 90) results.upcoming_90.push(entry);
  else results.safe.push(entry);
}
const totalActionable = results.expired.length + results.urgent_7.length + results.soon_30.length + results.upcoming_90.length;
const mostUrgent = results.expired.length > 0 ? "expired" : results.urgent_7.length > 0 ? "7_days" : results.soon_30.length > 0 ? "30_days" : results.upcoming_90.length > 0 ? "90_days" : "none";
return {
  expired_count: results.expired.length,
  urgent_7_count: results.urgent_7.length,
  soon_30_count: results.soon_30.length,
  upcoming_90_count: results.upcoming_90.length,
  total_actionable: totalActionable,
  most_urgent_level: mostUrgent,
  expired_items: JSON.stringify(results.expired.slice(0, 10)),
  urgent_items: JSON.stringify(results.urgent_7.concat(results.soon_30).slice(0, 20)),
  scan_summary: results.expired.length + " expired, " + results.urgent_7.length + " within 7 days, " + results.soon_30.length + " within 30 days, " + results.upcoming_90.length + " within 90 days"
};`,
          outputVariables: [
            { name: "expired_count", type: "number" },
            { name: "urgent_7_count", type: "number" },
            { name: "soon_30_count", type: "number" },
            { name: "upcoming_90_count", type: "number" },
            { name: "total_actionable", type: "number" },
            { name: "most_urgent_level", type: "string" },
            { name: "expired_items", type: "string" },
            { name: "urgent_items", type: "string" },
            { name: "scan_summary", type: "string" },
          ],
        },
      }),

      // 3. Urgency condition
      n("3", "condition", "Any expired or 7-day urgent items?", 520, {
        conditionData: [
          {
            variable: "most_urgent_level",
            value: "none",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Renewal reminder email
      emailNode(
        "4",
        "Send renewal reminders",
        760,
        "{ops_email}",
        "Document Renewal Alert: {scan_summary}",
        "Document/Insurance Renewal Scan Results\\n\\nSummary: {scan_summary}\\n\\nExpired Items ({expired_count}):\\n{expired_items}\\n\\nUrgent Items (within 30 days):\\n{urgent_items}\\n\\nPlease prioritize renewals starting with expired items, then 7-day urgencies.\\n\\nTotal items requiring action: {total_actionable}",
      ),

      // 5. Work task creation (AI)
      aiNode(
        "5",
        "AI: Create renewal work tasks",
        1000,
        "You are a task management specialist. Given expiring documents/policies, create prioritized work tasks with deadlines and assignees. Respond JSON: {tasks_created: number, task_list: string, priority_summary: string}",
        "Expired ({expired_count}): {expired_items}. Urgent ({urgent_7_count} within 7d, {soon_30_count} within 30d): {urgent_items}.",
        [
          { field: "tasks_created", as: "tasks_created", type: "number" },
          { field: "task_list", as: "task_list", type: "string" },
          { field: "priority_summary", as: "priority_summary", type: "string" },
        ],
      ),

      // 6. Expired SMS/Slack escalation
      smsNode(
        "6",
        "SMS: Escalate expired items",
        1240,
        "{ops_phone}",
        "URGENT: {expired_count} documents/policies have EXPIRED and {urgent_7_count} expire within 7 days. Immediate action required. Check your email for the full renewal report.",
      ),

      // 7. Renewal log (Sheets)
      sheetsNode(
        "7",
        "Log renewal scan (Sheets)",
        1480,
        "RenewalLog",
        '[["{most_urgent_level}","{expired_count}","{urgent_7_count}","{soon_30_count}","{upcoming_90_count}","{total_actionable}","{tasks_created}","{scan_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // has actionable items -> send reminders
      e("3", "7", "output_2"), // nothing actionable -> just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 632. INTERCOMPANY TRANSFER RECONCILIATION (8 nodes)
  // ===================================================================

  "Intercompany Transfer Reconciliation": {
    summary:
      "Reconciles intercompany transfers by pulling ledgers from both entities, computing matches/mismatches, auto-resolving timing differences, generating a reconciliation report, notifying controllers, creating tickets for large discrepancies, and maintaining an aging trail in Sheets.",
    nodes: [
      trigger("Schedule: Daily intercompany reconciliation", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6 AM to reconcile intercompany transfers between related entities by comparing both sides of each transaction",
        setup:
          "Configure APIs for both entity accounting systems. Set materiality thresholds for auto-resolution. Set up controller notification list and Sheets for reconciliation tracking.",
        inputFields: [],
      }),

      // 2. Both entity ledger pulls (API)
      aiNode(
        "2",
        "AI: Pull & normalize entity ledgers",
        280,
        "You are an intercompany accounting specialist. Simulate pulling and normalizing intercompany transaction ledgers from two entities. In production, these come from ERP API calls. Generate a representative dataset for reconciliation. Respond JSON: {entity_a_name: string, entity_b_name: string, entity_a_transactions: string, entity_b_transactions: string, entity_a_count: number, entity_b_count: number, date_range: string}",
        "Reconciliation date: today. Pull intercompany transactions from the last 30 days for both entities.",
        [
          { field: "entity_a_name", as: "entity_a_name", type: "string" },
          { field: "entity_b_name", as: "entity_b_name", type: "string" },
          {
            field: "entity_a_transactions",
            as: "entity_a_txns",
            type: "string",
          },
          {
            field: "entity_b_transactions",
            as: "entity_b_txns",
            type: "string",
          },
          { field: "entity_a_count", as: "entity_a_count", type: "number" },
          { field: "entity_b_count", as: "entity_b_count", type: "number" },
          { field: "date_range", as: "recon_date_range", type: "string" },
        ],
      ),

      // 3. Match/mismatch computation (runCode)
      n("3", "runCode", "Compute matches & mismatches", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match intercompany transactions between entities
const entityA = JSON.parse(vars.entity_a_txns || "[]");
const entityB = JSON.parse(vars.entity_b_txns || "[]");
const matched = [];
const unmatchedA = [...entityA];
const unmatchedB = [...entityB];
const TIMING_THRESHOLD_DAYS = 3;
const AMOUNT_TOLERANCE = 0.01;
// Match by reference, amount, and approximate date
for (let i = unmatchedA.length - 1; i >= 0; i--) {
  const a = unmatchedA[i];
  const matchIdx = unmatchedB.findIndex(b => {
    const amtMatch = Math.abs((parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0)) <= AMOUNT_TOLERANCE;
    const refMatch = a.reference && b.reference && a.reference === b.reference;
    return amtMatch && (refMatch || true);
  });
  if (matchIdx >= 0) {
    const b = unmatchedB[matchIdx];
    const dateDiff = Math.abs(new Date(a.date).getTime() - new Date(b.date).getTime()) / 86400000;
    matched.push({ a_ref: a.reference, b_ref: b.reference, amount: a.amount, date_diff_days: Math.round(dateDiff), is_timing: dateDiff <= TIMING_THRESHOLD_DAYS && dateDiff > 0 });
    unmatchedA.splice(i, 1);
    unmatchedB.splice(matchIdx, 1);
  }
}
const timingDiffs = matched.filter(m => m.is_timing).length;
const perfectMatches = matched.filter(m => !m.is_timing).length;
const totalDiscrepancies = unmatchedA.length + unmatchedB.length;
const largeDiscrepancies = unmatchedA.filter(a => Math.abs(parseFloat(a.amount) || 0) > 10000).length + unmatchedB.filter(b => Math.abs(parseFloat(b.amount) || 0) > 10000).length;
return {
  perfect_matches: perfectMatches,
  timing_differences: timingDiffs,
  unmatched_entity_a: unmatchedA.length,
  unmatched_entity_b: unmatchedB.length,
  total_discrepancies: totalDiscrepancies,
  large_discrepancy_count: largeDiscrepancies,
  has_timing_diffs: timingDiffs > 0 ? "yes" : "no",
  recon_summary: perfectMatches + " matched, " + timingDiffs + " timing diffs, " + totalDiscrepancies + " unmatched",
  unmatched_details: JSON.stringify(unmatchedA.concat(unmatchedB).slice(0, 20))
};`,
          outputVariables: [
            { name: "perfect_matches", type: "number" },
            { name: "timing_differences", type: "number" },
            { name: "unmatched_entity_a", type: "number" },
            { name: "unmatched_entity_b", type: "number" },
            { name: "total_discrepancies", type: "number" },
            { name: "large_discrepancy_count", type: "number" },
            { name: "has_timing_diffs", type: "string" },
            { name: "recon_summary", type: "string" },
            { name: "unmatched_details", type: "string" },
          ],
        },
      }),

      // 4. Timing auto-resolve condition
      n("4", "condition", "Has auto-resolvable timing diffs?", 760, {
        conditionData: [
          {
            variable: "has_timing_diffs",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Reconciliation report (AI)
      aiNode(
        "5",
        "AI: Generate reconciliation report",
        1000,
        "You are an intercompany reconciliation specialist. Given match results and unmatched items, generate a formal reconciliation report with auto-resolved timing differences, items requiring investigation, and recommended journal entries. Respond JSON: {report_text: string, auto_resolved: number, requires_investigation: number, recommended_entries: string, risk_assessment: string}",
        "Entities: {entity_a_name} vs {entity_b_name}. Period: {recon_date_range}. Results: {recon_summary}. Timing diffs: {timing_differences}. Large discrepancies: {large_discrepancy_count}. Unmatched details: {unmatched_details}.",
        [
          { field: "report_text", as: "recon_report", type: "string" },
          { field: "auto_resolved", as: "auto_resolved_count", type: "number" },
          {
            field: "requires_investigation",
            as: "investigation_count",
            type: "number",
          },
          {
            field: "recommended_entries",
            as: "recommended_entries",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
        ],
      ),

      // 6. Controller notification (email)
      emailNode(
        "6",
        "Notify controller",
        1240,
        "{controller_email}",
        "Intercompany Reconciliation: {recon_summary}",
        "Intercompany Reconciliation Report\\n\\nEntities: {entity_a_name} vs {entity_b_name}\\nPeriod: {recon_date_range}\\n\\nResults Summary: {recon_summary}\\nAuto-Resolved: {auto_resolved_count}\\nRequires Investigation: {investigation_count}\\nLarge Discrepancies: {large_discrepancy_count}\\n\\nReport:\\n{recon_report}\\n\\nRecommended Journal Entries:\\n{recommended_entries}\\n\\nRisk Assessment: {risk_assessment}",
      ),

      // 7. Large discrepancy tickets (Slack)
      slackNode(
        "7",
        "Slack: Large discrepancy alert",
        1480,
        ":rotating_light: Intercompany Reconciliation Alert\\n{entity_a_name} vs {entity_b_name} | {recon_date_range}\\nMatched: {perfect_matches} | Timing: {timing_differences} | Unmatched: {total_discrepancies}\\nLarge Discrepancies: {large_discrepancy_count}\\nRisk: {risk_assessment}",
      ),

      // 8. Aging trail (Sheets)
      sheetsNode(
        "8",
        "Log reconciliation trail (Sheets)",
        1720,
        "IntercompanyRecon",
        '[["{entity_a_name}","{entity_b_name}","{recon_date_range}","{perfect_matches}","{timing_differences}","{total_discrepancies}","{large_discrepancy_count}","{auto_resolved_count}","{investigation_count}","{risk_assessment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // timing diffs -> include auto-resolution in report
      e("4", "5", "output_2"), // no timing diffs -> standard report
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 633. INVOICE AUTO-SENDER (8 nodes)
  // ===================================================================

  "Invoice Auto-Sender": {
    summary:
      "Automatically generates and sends invoices at billing milestones. A milestone/billing trigger starts the flow, billable data is pulled, a Stripe invoice is generated, the invoice email with payment link is sent, AR logging records to Sheets, an unpaid reminder delay triggers, an escalation condition checks payment status, and a collections alert fires for overdue items.",
    nodes: [
      trigger("Webhook: Billing milestone reached", "webhook_app", {
        source: "Project Management / CRM",
        event: "billing.milestone.reached",
        howItStarts:
          "A project milestone is completed or a billing cycle date arrives, triggering automatic invoice generation and delivery",
        setup:
          "Configure project management or CRM webhook for milestone completion events. Include client details, billable amounts, line items, and payment terms.",
        inputFields: [],
      }),

      // 2. Billable data pull (AI extraction)
      aiNode(
        "2",
        "AI: Extract billable data",
        280,
        "You are an invoicing specialist. Parse the billing trigger payload to extract all billable information: client details, line items, amounts, payment terms, and due date. Calculate totals including any applicable tax. Respond JSON: {client_email: string, client_name: string, client_company: string, line_items: string, subtotal: number, tax_amount: number, total_amount: number, currency: string, payment_terms_days: number, due_date: string, invoice_memo: string, project_name: string}",
        "Billing milestone payload: {webhook_payload}",
        [
          { field: "client_email", as: "client_email", type: "string" },
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_company", as: "client_company", type: "string" },
          { field: "line_items", as: "line_items_json", type: "string" },
          { field: "subtotal", as: "subtotal", type: "number" },
          { field: "tax_amount", as: "tax_amount", type: "number" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          {
            field: "payment_terms_days",
            as: "payment_terms_days",
            type: "number",
          },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "invoice_memo", as: "invoice_memo", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
        ],
      ),

      // 3. Invoice generation (Stripe)
      stripeNode(
        "3",
        "Create invoice (Stripe)",
        520,
        "POST",
        "invoices",
        [
          { key: "customer", value: "{stripe_customer_id}" },
          { key: "collection_method", value: "send_invoice" },
          { key: "days_until_due", value: "{payment_terms_days}" },
          { key: "description", value: "{project_name} - {invoice_memo}" },
        ],
        [
          { name: "id", customName: "stripe_invoice_id", type: "string" },
          {
            name: "hosted_invoice_url",
            customName: "payment_link",
            type: "string",
          },
          { name: "number", customName: "invoice_number", type: "string" },
          { name: "status", customName: "invoice_status", type: "string" },
        ],
      ),

      // 4. Invoice email with payment link
      emailNode(
        "4",
        "Send invoice to client",
        760,
        "{client_email}",
        "Invoice {invoice_number}: {currency} {total_amount} for {project_name}",
        "Dear {client_name},\\n\\nPlease find your invoice below.\\n\\nInvoice Number: {invoice_number}\\nProject: {project_name}\\nAmount Due: {currency} {total_amount}\\nDue Date: {due_date}\\nPayment Terms: Net {payment_terms_days}\\n\\n{invoice_memo}\\n\\nPay Online: {payment_link}\\n\\nThank you for your business.",
      ),

      // 5. AR logging (Sheets)
      sheetsNode(
        "5",
        "Log to AR ledger (Sheets)",
        1000,
        "AccountsReceivable",
        '[["{stripe_invoice_id}","{invoice_number}","{client_company}","{client_email}","{total_amount}","{currency}","{due_date}","{invoice_status}","{project_name}","sent"]]',
      ),

      // 6. Unpaid reminder delay
      n("6", "delayAction", "Wait for payment terms period", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "{payment_terms_days}",
          delayUnit: "days",
        },
      }),

      // 7. Escalation condition
      n("7", "condition", "Invoice still unpaid?", 1480, {
        conditionData: [
          {
            variable: "invoice_status",
            value: "paid",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Collections alert
      slackNode(
        "8",
        "Slack: Collections escalation",
        1720,
        ":warning: Invoice Overdue - Collections Alert\\nInvoice: {invoice_number} | Client: {client_company}\\nAmount: {currency} {total_amount} | Due: {due_date}\\nProject: {project_name}\\nPayment link: {payment_link}\\nPlease follow up with the client immediately.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // unpaid -> collections escalation
      e("7", "5", "output_2"), // paid -> update AR log (closes loop)
    ],
  },

  // ===================================================================
  // 634. INVOICE DUE REMINDER (7 nodes)
  // ===================================================================

  "Invoice Due Reminder": {
    summary:
      "Daily scan of outstanding invoices with aging classification and tone-appropriate reminders. A daily schedule triggers scanning, aging classification runs via runCode, tone is selected via condition, reminder emails are sent, reminder history is updated in Sheets, 7+ day overdue items escalate, and a weekly aging summary email is generated.",
    nodes: [
      trigger("Schedule: Daily invoice due scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to scan all outstanding invoices and send age-appropriate payment reminders",
        setup:
          "Configure Stripe API or accounting system for outstanding invoice data. Set up reminder history tracking in Sheets. Define escalation contacts for overdue invoices.",
        inputFields: [],
      }),

      // 2. Aging classification (runCode)
      n("2", "runCode", "Classify invoices by aging bucket", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify outstanding invoices into aging buckets
// In production, this reads from Stripe or accounting API
const invoices = JSON.parse(vars.outstanding_invoices || "[]");
const now = new Date();
const DAY = 86400000;
const buckets = { current: [], overdue_1_7: [], overdue_8_30: [], overdue_31_60: [], overdue_60plus: [] };
for (const inv of invoices) {
  const due = new Date(inv.due_date);
  const daysOverdue = Math.floor((now.getTime() - due.getTime()) / DAY);
  const entry = { ...inv, days_overdue: daysOverdue };
  if (daysOverdue <= 0) buckets.current.push(entry);
  else if (daysOverdue <= 7) buckets.overdue_1_7.push(entry);
  else if (daysOverdue <= 30) buckets.overdue_8_30.push(entry);
  else if (daysOverdue <= 60) buckets.overdue_31_60.push(entry);
  else buckets.overdue_60plus.push(entry);
}
const overdueTotal = buckets.overdue_1_7.length + buckets.overdue_8_30.length + buckets.overdue_31_60.length + buckets.overdue_60plus.length;
const mostSevere = buckets.overdue_60plus.length > 0 ? "60plus" : buckets.overdue_31_60.length > 0 ? "31_60" : buckets.overdue_8_30.length > 0 ? "8_30" : buckets.overdue_1_7.length > 0 ? "1_7" : "current";
const tone = mostSevere === "60plus" ? "final_notice" : mostSevere === "31_60" ? "firm" : mostSevere === "8_30" ? "concerned" : "friendly";
const needsEscalation = buckets.overdue_8_30.length + buckets.overdue_31_60.length + buckets.overdue_60plus.length > 0 ? "yes" : "no";
return {
  current_count: buckets.current.length,
  overdue_1_7_count: buckets.overdue_1_7.length,
  overdue_8_30_count: buckets.overdue_8_30.length,
  overdue_31_60_count: buckets.overdue_31_60.length,
  overdue_60plus_count: buckets.overdue_60plus.length,
  total_overdue: overdueTotal,
  most_severe_bucket: mostSevere,
  recommended_tone: tone,
  needs_escalation: needsEscalation,
  aging_summary: buckets.current.length + " current, " + overdueTotal + " overdue (" + buckets.overdue_1_7.length + " <7d, " + buckets.overdue_8_30.length + " 8-30d, " + buckets.overdue_31_60.length + " 31-60d, " + buckets.overdue_60plus.length + " >60d)"
};`,
          outputVariables: [
            { name: "current_count", type: "number" },
            { name: "overdue_1_7_count", type: "number" },
            { name: "overdue_8_30_count", type: "number" },
            { name: "overdue_31_60_count", type: "number" },
            { name: "overdue_60plus_count", type: "number" },
            { name: "total_overdue", type: "number" },
            { name: "most_severe_bucket", type: "string" },
            { name: "recommended_tone", type: "string" },
            { name: "needs_escalation", type: "string" },
            { name: "aging_summary", type: "string" },
          ],
        },
      }),

      // 3. Tone selection condition
      n("3", "condition", "Overdue invoices needing escalation?", 520, {
        conditionData: [
          {
            variable: "needs_escalation",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Reminder email
      emailNode(
        "4",
        "Send payment reminder",
        760,
        "{billing_contact_email}",
        "Payment Reminder: {total_overdue} Invoices Outstanding",
        "Payment Reminder\\n\\nThis is a {recommended_tone} reminder regarding outstanding invoices.\\n\\nAging Summary: {aging_summary}\\n\\nPlease process outstanding payments at your earliest convenience. If payment has already been sent, please disregard this notice.\\n\\nFor questions about specific invoices, contact our billing team.",
      ),

      // 5. Reminder history update (Sheets)
      sheetsNode(
        "5",
        "Update reminder history (Sheets)",
        1000,
        "ReminderHistory",
        '[["{total_overdue}","{recommended_tone}","{most_severe_bucket}","{aging_summary}","sent"]]',
      ),

      // 6. 7+ day escalation
      slackNode(
        "6",
        "Slack: Overdue escalation",
        1240,
        ":red_circle: Invoice Escalation Required\\nTotal Overdue: {total_overdue}\\n8-30 days: {overdue_8_30_count} | 31-60 days: {overdue_31_60_count} | 60+ days: {overdue_60plus_count}\\nTone: {recommended_tone}\\nAction: Review and initiate direct follow-up calls for 30+ day overdue invoices.",
      ),

      // 7. Weekly aging summary (email)
      emailNode(
        "7",
        "Send weekly aging summary",
        1480,
        "{finance_team_email}",
        "Weekly Invoice Aging Summary: {total_overdue} Overdue",
        "Weekly Invoice Aging Report\\n\\nAging Buckets:\\n- Current (not yet due): {current_count}\\n- 1-7 days overdue: {overdue_1_7_count}\\n- 8-30 days overdue: {overdue_8_30_count}\\n- 31-60 days overdue: {overdue_31_60_count}\\n- 60+ days overdue: {overdue_60plus_count}\\n\\nTotal Overdue: {total_overdue}\\nMost Severe Bucket: {most_severe_bucket}\\n\\nRecommended Action: Focus collection efforts on {most_severe_bucket} bucket items.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // needs escalation -> send reminder + escalate
      e("3", "5", "output_2"), // no escalation needed -> just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 635. INVOICE PAID CONFIRMATION (6 nodes)
  // ===================================================================

  "Invoice Paid Confirmation": {
    summary:
      "Confirms payment receipt and updates records when a Stripe invoice is paid. A Stripe payment confirmation triggers the flow, invoice and payment data is assembled, a receipt/confirmation email is sent, AR is marked paid in Sheets, notable payments are checked via condition, and finance is notified in Slack for large payments.",
    nodes: [
      trigger("Webhook: Stripe invoice paid", "webhook_app", {
        source: "Stripe",
        event: "invoice.paid",
        howItStarts:
          "Stripe fires a webhook when an invoice payment is successfully processed, triggering confirmation and record updates",
        setup:
          "Configure Stripe webhook for invoice.paid events. Include invoice details, customer info, and payment method data.",
        inputFields: [],
      }),

      // 2. Invoice/payment assembly (runCode)
      n("2", "runCode", "Assemble payment confirmation data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Stripe invoice.paid webhook and assemble confirmation data
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customerEmail = data.customer_email || (data.customer_details || {}).email || "";
const customerName = data.customer_name || (data.customer_details || {}).name || "Customer";
const invoiceNumber = data.number || data.id || "";
const invoiceId = data.id || "";
const amountPaid = ((data.amount_paid || data.total || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const paidAt = data.status_transitions ? new Date(data.status_transitions.paid_at * 1000).toISOString() : new Date().toISOString();
const paymentMethod = data.payment_intent ? "card" : "other";
const description = data.description || data.lines ? (data.lines.data || []).map(l => l.description).join(", ") : "";
const isNotable = parseFloat(amountPaid) >= 5000 ? "yes" : "no";
return {
  customer_email: customerEmail,
  customer_name: customerName,
  invoice_number: invoiceNumber,
  invoice_id: invoiceId,
  amount_paid: amountPaid,
  currency: currency,
  paid_at: paidAt,
  payment_method: paymentMethod,
  invoice_description: description,
  is_notable_payment: isNotable
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "invoice_number", type: "string" },
            { name: "invoice_id", type: "string" },
            { name: "amount_paid", type: "string" },
            { name: "currency", type: "string" },
            { name: "paid_at", type: "string" },
            { name: "payment_method", type: "string" },
            { name: "invoice_description", type: "string" },
            { name: "is_notable_payment", type: "string" },
          ],
        },
      }),

      // 3. Receipt/confirmation email
      emailNode(
        "3",
        "Send payment confirmation",
        520,
        "{customer_email}",
        "Payment Confirmed: {currency} {amount_paid} - Invoice {invoice_number}",
        "Dear {customer_name},\\n\\nThank you for your payment!\\n\\nInvoice: {invoice_number}\\nAmount Paid: {currency} {amount_paid}\\nDate: {paid_at}\\nPayment Method: {payment_method}\\n\\nDescription: {invoice_description}\\n\\nThis serves as your official payment receipt. No further action is needed.\\n\\nThank you for your prompt payment.",
      ),

      // 4. AR mark-paid (Sheets)
      sheetsNode(
        "4",
        "Mark invoice paid in AR (Sheets)",
        760,
        "AccountsReceivable",
        '[["{invoice_id}","{invoice_number}","{customer_email}","{amount_paid}","{currency}","{paid_at}","paid","{payment_method}"]]',
      ),

      // 5. Notable payment condition
      n("5", "condition", "Notable payment (>=$5,000)?", 1000, {
        conditionData: [
          {
            variable: "is_notable_payment",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Finance Slack notification
      slackNode(
        "6",
        "Slack: Notable payment received",
        1240,
        ":white_check_mark: Notable Payment Received\\nInvoice: {invoice_number} | {customer_name}\\nAmount: {currency} {amount_paid}\\nPaid: {paid_at}\\nDescription: {invoice_description}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // notable -> Slack notification
      e("5", "6", "output_2"), // not notable -> still log but low-key
    ],
  },

  // ===================================================================
  // 636. INVOICE PROCESSING AGENT (9 nodes)
  // ===================================================================

  "Invoice Processing Agent": {
    summary:
      "End-to-end invoice processing from receipt to payment scheduling. A vendor invoice trigger starts the flow, AI extracts invoice data, vendor is verified, duplicates are checked via condition, PO matching runs, approval routing is applied, payment is scheduled, the AP ledger is updated in Sheets, and processing results are notified.",
    nodes: [
      trigger("Webhook: Vendor invoice received", "webhook_app", {
        source: "Email / AP Portal",
        event: "invoice.received",
        howItStarts:
          "A vendor invoice is received via email, AP portal upload, or EDI, triggering the automated processing pipeline",
        setup:
          "Configure email forwarding or AP portal webhook for incoming invoices. Include invoice document/image and sender details.",
        inputFields: [],
      }),

      // 2. AI data extraction
      aiNode(
        "2",
        "AI: Extract invoice data",
        280,
        "You are an accounts payable specialist with expertise in invoice data extraction. Parse the incoming invoice to extract all fields: vendor name, vendor ID, invoice number, invoice date, due date, line items with descriptions and amounts, subtotal, tax, total, payment terms, PO number if referenced, currency, and bank details. Handle any invoice format. Respond JSON: {vendor_name: string, vendor_email: string, vendor_id: string, invoice_number: string, invoice_date: string, due_date: string, line_items: string, subtotal: number, tax_amount: number, total_amount: number, currency: string, payment_terms: string, po_number: string, bank_details: string}",
        "Vendor invoice payload: {webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_email", as: "vendor_email", type: "string" },
          { field: "vendor_id", as: "vendor_id", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "invoice_date", as: "invoice_date", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "line_items", as: "line_items_json", type: "string" },
          { field: "subtotal", as: "subtotal", type: "number" },
          { field: "tax_amount", as: "tax_amount", type: "number" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "po_number", as: "po_number", type: "string" },
          { field: "bank_details", as: "bank_details", type: "string" },
        ],
      ),

      // 3. Vendor verification
      aiNode(
        "3",
        "AI: Verify vendor & compliance",
        520,
        "You are a vendor management specialist. Verify the vendor against the approved vendor list, check for any compliance holds or risk flags, and validate W-9/tax status. Respond JSON: {vendor_verified: boolean, vendor_status: string, compliance_flags: string, risk_level: string, verification_notes: string}",
        "Vendor: {vendor_name} (ID: {vendor_id}). Email: {vendor_email}. Invoice: {invoice_number} for {currency} {total_amount}.",
        [
          { field: "vendor_verified", as: "vendor_verified", type: "string" },
          { field: "vendor_status", as: "vendor_status", type: "string" },
          {
            field: "compliance_flags",
            as: "vendor_compliance_flags",
            type: "string",
          },
          { field: "risk_level", as: "vendor_risk_level", type: "string" },
          {
            field: "verification_notes",
            as: "verification_notes",
            type: "string",
          },
        ],
      ),

      // 4. Duplicate check condition
      n("4", "condition", "Potential duplicate invoice?", 760, {
        conditionData: [
          {
            variable: "vendor_verified",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. PO matching
      aiNode(
        "5",
        "AI: Match to purchase order",
        1000,
        "You are a procurement matching specialist. Match the invoice line items against the referenced PO or find the best-matching open PO. Check for quantity, price, and description matches. Respond JSON: {po_matched: boolean, po_match_score: number, matched_po: string, line_discrepancies: string, approval_amount_threshold: string, requires_manager_approval: boolean}",
        "Invoice from {vendor_name}: {invoice_number}. PO ref: {po_number}. Line items: {line_items_json}. Total: {currency} {total_amount}.",
        [
          { field: "po_matched", as: "po_matched", type: "string" },
          { field: "po_match_score", as: "po_match_score", type: "number" },
          { field: "matched_po", as: "matched_po", type: "string" },
          {
            field: "line_discrepancies",
            as: "line_discrepancies",
            type: "string",
          },
          {
            field: "approval_amount_threshold",
            as: "approval_threshold",
            type: "string",
          },
          {
            field: "requires_manager_approval",
            as: "requires_approval",
            type: "string",
          },
        ],
      ),

      // 6. Approval routing
      emailNode(
        "6",
        "Route for approval",
        1240,
        "{ap_manager_email}",
        "Invoice Approval: {vendor_name} - {currency} {total_amount} (#{invoice_number})",
        "Invoice Approval Request\\n\\nVendor: {vendor_name}\\nInvoice: {invoice_number}\\nDate: {invoice_date} | Due: {due_date}\\nAmount: {currency} {total_amount}\\n\\nPO Match: {matched_po} (Score: {po_match_score}%)\\nDiscrepancies: {line_discrepancies}\\n\\nVendor Status: {vendor_status}\\nRisk Level: {vendor_risk_level}\\nCompliance Flags: {vendor_compliance_flags}\\n\\nPlease approve or reject this invoice for payment scheduling.",
      ),

      // 7. Payment scheduling
      n("7", "runCode", "Schedule payment", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Schedule payment based on terms and early payment discounts
const dueDate = new Date(vars.due_date || new Date());
const total = parseFloat(vars.total_amount) || 0;
const terms = vars.payment_terms || "Net 30";
// Check for early payment discount (e.g., 2/10 Net 30)
const discountMatch = terms.match(/(\\d+)\\/(\\d+)/);
let paymentDate = dueDate;
let paymentAmount = total;
let discountTaken = false;
if (discountMatch) {
  const discountPct = parseFloat(discountMatch[1]);
  const discountDays = parseInt(discountMatch[2]);
  const discountDeadline = new Date(vars.invoice_date);
  discountDeadline.setDate(discountDeadline.getDate() + discountDays);
  if (discountDeadline > new Date()) {
    paymentAmount = total * (1 - discountPct / 100);
    paymentDate = discountDeadline;
    discountTaken = true;
  }
}
return {
  scheduled_payment_date: paymentDate.toISOString().split("T")[0],
  scheduled_payment_amount: paymentAmount.toFixed(2),
  discount_taken: discountTaken ? "yes" : "no",
  discount_savings: discountTaken ? (total - paymentAmount).toFixed(2) : "0.00",
  payment_status: "scheduled"
};`,
          outputVariables: [
            { name: "scheduled_payment_date", type: "string" },
            { name: "scheduled_payment_amount", type: "string" },
            { name: "discount_taken", type: "string" },
            { name: "discount_savings", type: "string" },
            { name: "payment_status", type: "string" },
          ],
        },
      }),

      // 8. AP ledger update (Sheets)
      sheetsNode(
        "8",
        "Update AP ledger (Sheets)",
        1720,
        "AccountsPayable",
        '[["{invoice_number}","{vendor_name}","{invoice_date}","{due_date}","{total_amount}","{currency}","{po_number}","{matched_po}","{scheduled_payment_date}","{scheduled_payment_amount}","{discount_taken}","{payment_status}"]]',
      ),

      // 9. Processing results notification
      slackNode(
        "9",
        "Slack: Invoice processing complete",
        1960,
        ":receipt: Invoice Processed\\nVendor: {vendor_name} | #{invoice_number}\\nAmount: {currency} {total_amount} | PO: {matched_po}\\nPayment: {scheduled_payment_date} for ${scheduled_payment_amount}\\nDiscount: {discount_taken} (saved ${discount_savings})\\nStatus: {vendor_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // vendor verified -> PO matching
      e("4", "6", "output_2"), // vendor not verified -> route for manual review
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 637. INVOICE REMINDER DRIP (8 nodes)
  // ===================================================================

  "Invoice Reminder Drip": {
    summary:
      "Multi-stage drip sequence for invoice collections from courtesy to escalation. An invoice issued trigger starts the flow, a pre-due courtesy email is sent, payment is checked at due date via condition, an overdue notice is sent, a delay and second check follow, a second overdue notice is sent, SMS fires at a later stage, and collections escalation with logging completes the flow.",
    nodes: [
      trigger("Webhook: Invoice issued", "webhook_app", {
        source: "Stripe / Billing System",
        event: "invoice.created",
        howItStarts:
          "A new invoice is issued, starting the reminder drip sequence that escalates from courtesy to collections",
        setup:
          "Configure Stripe or billing webhook for invoice.created events. Ensure customer contact info (email, phone) is available.",
        inputFields: [],
      }),

      // 2. Pre-due courtesy email (AI extraction first)
      aiNode(
        "2",
        "AI: Extract invoice & start drip",
        280,
        "You are a billing communications specialist. Extract invoice details from the payload and prepare the initial courtesy reminder. Respond JSON: {customer_email: string, customer_phone: string, customer_name: string, invoice_number: string, amount_due: string, currency: string, due_date: string, payment_link: string, days_until_due: number}",
        "Invoice issued payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "amount_due", as: "amount_due", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "payment_link", as: "payment_link", type: "string" },
          { field: "days_until_due", as: "days_until_due", type: "number" },
        ],
      ),

      // 3. Due-date payment check condition (after initial delay)
      n("3", "condition", "Payment received by due date?", 520, {
        conditionData: [
          {
            variable: "days_until_due",
            value: "0",
            comparison_operator: "LESS THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Overdue notice 1
      emailNode(
        "4",
        "Send overdue notice #1",
        760,
        "{customer_email}",
        "Friendly Reminder: Invoice {invoice_number} is Past Due",
        "Hi {customer_name},\\n\\nThis is a friendly reminder that invoice {invoice_number} for {currency} {amount_due} was due on {due_date} and remains unpaid.\\n\\nPay Now: {payment_link}\\n\\nIf payment has already been sent, please disregard this notice. If you have questions, please contact our billing team.",
      ),

      // 5. Delay + check
      n("5", "delayAction", "Wait 7 days for payment", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "7",
          delayUnit: "days",
        },
      }),

      // 6. Overdue notice 2
      emailNode(
        "6",
        "Send overdue notice #2",
        1240,
        "{customer_email}",
        "Second Notice: Invoice {invoice_number} - 7+ Days Overdue",
        "Dear {customer_name},\\n\\nThis is a second notice regarding invoice {invoice_number} for {currency} {amount_due}, which is now more than 7 days past due.\\n\\nImmediate payment is required to avoid service interruption.\\n\\nPay Now: {payment_link}\\n\\nPlease contact our billing team if you are experiencing payment difficulties.",
      ),

      // 7. SMS at later stage
      smsNode(
        "7",
        "SMS: Urgent payment reminder",
        1480,
        "{customer_phone}",
        "URGENT: Invoice {invoice_number} for {currency} {amount_due} is significantly overdue. Please pay immediately at {payment_link} or contact us to arrange payment.",
      ),

      // 8. Collections escalation + logging (Sheets)
      sheetsNode(
        "8",
        "Log collections escalation (Sheets)",
        1720,
        "CollectionsLog",
        '[["{invoice_number}","{customer_name}","{customer_email}","{amount_due}","{currency}","{due_date}","escalated","sms_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // due date passed -> overdue notice
      e("3", "8", "output_2"), // paid on time -> log and close
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 638. INVOICE REMINDER SYSTEM (7 nodes)
  // ===================================================================

  "Invoice Reminder System": {
    summary:
      "Systematic invoice reminder system with aging-based tone selection and escalation. A daily overdue scan triggers prior reminder counting via runCode, aging tone is selected via condition, reminder emails are sent, reminders are logged to Sheets, stubborn cases escalate in Slack, and a weekly overdue summary email is generated.",
    nodes: [
      trigger("Schedule: Daily overdue invoice scan", "schedule", {
        schedule: "0 10 * * *",
        howItStarts:
          "Runs daily at 10 AM to scan overdue invoices, count prior reminders, and send appropriately toned follow-ups",
        setup:
          "Configure accounting API or Stripe for overdue invoice data. Set up reminder history in Sheets for tracking contact frequency. Define stubborn threshold (e.g., 3+ reminders without payment).",
        inputFields: [],
      }),

      // 2. Prior reminder count (runCode)
      n("2", "runCode", "Count prior reminders & classify", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Count prior reminders and determine appropriate messaging
const invoices = JSON.parse(vars.overdue_invoices || "[]");
const reminderHistory = JSON.parse(vars.reminder_history || "{}");
const now = new Date();
const DAY = 86400000;
const actionable = [];
let stubbornCount = 0;
for (const inv of invoices) {
  const dueDate = new Date(inv.due_date);
  const daysOverdue = Math.floor((now.getTime() - dueDate.getTime()) / DAY);
  const priorReminders = (reminderHistory[inv.id] || []).length;
  const lastReminder = (reminderHistory[inv.id] || []).slice(-1)[0];
  const daysSinceLastReminder = lastReminder ? Math.floor((now.getTime() - new Date(lastReminder).getTime()) / DAY) : 999;
  // Only send if enough time has passed since last reminder
  if (daysSinceLastReminder >= 3) {
    const tone = priorReminders === 0 ? "friendly" : priorReminders <= 2 ? "firm" : priorReminders <= 4 ? "urgent" : "final_notice";
    const isStubborn = priorReminders >= 3;
    if (isStubborn) stubbornCount++;
    actionable.push({ ...inv, days_overdue: daysOverdue, prior_reminders: priorReminders, tone: tone, is_stubborn: isStubborn });
  }
}
const totalActionable = actionable.length;
const hasStubbornCases = stubbornCount > 0 ? "yes" : "no";
return {
  actionable_count: totalActionable,
  stubborn_count: stubbornCount,
  has_stubborn_cases: hasStubbornCases,
  actionable_invoices: JSON.stringify(actionable.slice(0, 20)),
  reminder_summary: totalActionable + " reminders to send (" + stubbornCount + " stubborn cases with 3+ prior reminders)"
};`,
          outputVariables: [
            { name: "actionable_count", type: "number" },
            { name: "stubborn_count", type: "number" },
            { name: "has_stubborn_cases", type: "string" },
            { name: "actionable_invoices", type: "string" },
            { name: "reminder_summary", type: "string" },
          ],
        },
      }),

      // 3. Aging tone selection condition
      n("3", "condition", "Stubborn cases requiring escalation?", 520, {
        conditionData: [
          {
            variable: "has_stubborn_cases",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Reminder email
      emailNode(
        "4",
        "Send tone-appropriate reminders",
        760,
        "{billing_email}",
        "Invoice Reminders Queued: {actionable_count} Overdue Invoices",
        "Invoice Reminder Batch\\n\\nTotal Reminders Queued: {actionable_count}\\nStubborn Cases (3+ priors): {stubborn_count}\\n\\nSummary: {reminder_summary}\\n\\nInvoice details: {actionable_invoices}\\n\\nReminders will be sent with age-appropriate tone (friendly, firm, urgent, or final notice).",
      ),

      // 5. Reminder logging (Sheets)
      sheetsNode(
        "5",
        "Log reminders sent (Sheets)",
        1000,
        "ReminderLog",
        '[["{actionable_count}","{stubborn_count}","{reminder_summary}","batch_sent"]]',
      ),

      // 6. Stubborn case escalation (Slack)
      slackNode(
        "6",
        "Slack: Stubborn case escalation",
        1240,
        ":red_circle: Stubborn Invoice Alert\\n{stubborn_count} invoices have received 3+ reminders without payment.\\n{reminder_summary}\\nAction: Assign to collections team for phone follow-up.",
      ),

      // 7. Weekly overdue summary (email)
      emailNode(
        "7",
        "Weekly overdue summary email",
        1480,
        "{finance_team_email}",
        "Weekly Overdue Invoice Summary: {actionable_count} Outstanding",
        "Weekly Overdue Invoice Summary\\n\\nActionable Invoices: {actionable_count}\\nStubborn Cases: {stubborn_count}\\n\\nSummary: {reminder_summary}\\n\\nRecommendation: Focus collections effort on the {stubborn_count} stubborn cases that have not responded to multiple reminders.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // stubborn cases -> send + escalate
      e("3", "4", "output_2"), // no stubborn -> send reminders normally
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 639. LEGAL BILLING TIME TRACKER (8 nodes)
  // ===================================================================

  "Legal Billing Time Tracker": {
    summary:
      "Tracks and validates legal time entries with rate card application, budget impact checks, and WIP summaries. A time entry trigger starts the flow, matter/client is validated, activity is classified by AI, rate card is applied via runCode, budget impact is checked via condition, time entry is recorded to Sheets, budget exhaustion alerts go to Slack, and a weekly WIP summary is emailed.",
    nodes: [
      trigger("Webhook: Legal time entry submitted", "webhook_app", {
        source: "Time Tracking / Legal Practice",
        event: "time.entry.submitted",
        howItStarts:
          "An attorney or legal staff submits a time entry, triggering validation, rate application, and budget tracking against the matter budget",
        setup:
          "Configure time tracking webhook for new entry submissions. Include matter number, attorney ID, activity description, and duration. Set up rate cards and budget limits per matter.",
        inputFields: [],
      }),

      // 2. Matter/client validation
      aiNode(
        "2",
        "AI: Validate matter & extract entry",
        280,
        "You are a legal billing specialist. Parse the time entry submission, validate the matter/client reference, and extract all billing details. Respond JSON: {attorney_email: string, attorney_name: string, attorney_id: string, matter_number: string, client_name: string, activity_description: string, duration_hours: number, entry_date: string, matter_budget: number, matter_spent_to_date: number, billing_type: string}",
        "Time entry payload: {webhook_payload}",
        [
          { field: "attorney_email", as: "attorney_email", type: "string" },
          { field: "attorney_name", as: "attorney_name", type: "string" },
          { field: "attorney_id", as: "attorney_id", type: "string" },
          { field: "matter_number", as: "matter_number", type: "string" },
          { field: "client_name", as: "client_name", type: "string" },
          {
            field: "activity_description",
            as: "activity_description",
            type: "string",
          },
          { field: "duration_hours", as: "duration_hours", type: "number" },
          { field: "entry_date", as: "entry_date", type: "string" },
          { field: "matter_budget", as: "matter_budget", type: "number" },
          {
            field: "matter_spent_to_date",
            as: "matter_spent_to_date",
            type: "number",
          },
          { field: "billing_type", as: "billing_type", type: "string" },
        ],
      ),

      // 3. Activity classification (AI)
      aiNode(
        "3",
        "AI: Classify activity & narrative",
        520,
        "You are a legal billing narrative specialist. Classify the time entry activity into standard UTBMS task codes, improve the narrative for billing compliance, and flag any entries that may be challenged by clients (block billing, vague descriptions, excessive time). Respond JSON: {utbms_code: string, activity_category: string, improved_narrative: string, billing_flags: string, is_billable: boolean, write_down_risk: string}",
        "Attorney: {attorney_name}. Matter: {matter_number} for {client_name}. Activity: {activity_description}. Duration: {duration_hours} hours. Type: {billing_type}.",
        [
          { field: "utbms_code", as: "utbms_code", type: "string" },
          {
            field: "activity_category",
            as: "activity_category",
            type: "string",
          },
          {
            field: "improved_narrative",
            as: "improved_narrative",
            type: "string",
          },
          { field: "billing_flags", as: "billing_flags", type: "string" },
          { field: "is_billable", as: "is_billable", type: "string" },
          { field: "write_down_risk", as: "write_down_risk", type: "string" },
        ],
      ),

      // 4. Rate card application (runCode)
      n("4", "runCode", "Apply rate card & compute budget impact", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply attorney rate card and compute budget impact
const hours = parseFloat(vars.duration_hours) || 0;
// Rate card lookup - in production from database
const seniorRates = { partner: 650, senior_associate: 450, associate: 350, paralegal: 200 };
const attorneyId = vars.attorney_id || "";
const rate = seniorRates.associate; // Default, overridden by lookup
const entryValue = hours * rate;
const budget = parseFloat(vars.matter_budget) || 0;
const spent = parseFloat(vars.matter_spent_to_date) || 0;
const newSpent = spent + entryValue;
const remaining = budget - newSpent;
const budgetPct = budget > 0 ? (newSpent / budget * 100) : 0;
const budgetStatus = budgetPct >= 100 ? "exceeded" : budgetPct >= 90 ? "critical" : budgetPct >= 75 ? "warning" : "healthy";
const needsBudgetAlert = budgetPct >= 75 ? "yes" : "no";
return {
  hourly_rate: rate,
  entry_value: entryValue.toFixed(2),
  new_matter_spent: newSpent.toFixed(2),
  matter_remaining: remaining.toFixed(2),
  budget_utilization_pct: budgetPct.toFixed(1),
  budget_status: budgetStatus,
  needs_budget_alert: needsBudgetAlert
};`,
          outputVariables: [
            { name: "hourly_rate", type: "number" },
            { name: "entry_value", type: "string" },
            { name: "new_matter_spent", type: "string" },
            { name: "matter_remaining", type: "string" },
            { name: "budget_utilization_pct", type: "string" },
            { name: "budget_status", type: "string" },
            { name: "needs_budget_alert", type: "string" },
          ],
        },
      }),

      // 5. Budget impact check condition
      n("5", "condition", "Budget at 75%+ utilization?", 1000, {
        conditionData: [
          {
            variable: "needs_budget_alert",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Time entry recording (Sheets)
      sheetsNode(
        "6",
        "Record time entry (Sheets)",
        1240,
        "LegalTimeEntries",
        '[["{entry_date}","{attorney_name}","{matter_number}","{client_name}","{improved_narrative}","{utbms_code}","{duration_hours}","{hourly_rate}","{entry_value}","{budget_status}","{billing_flags}"]]',
      ),

      // 7. Budget exhaustion alert (Slack)
      slackNode(
        "7",
        "Slack: Budget exhaustion alert",
        1480,
        ":scales: Legal Matter Budget Alert\\nMatter: {matter_number} ({client_name})\\nBudget: {budget_status} ({budget_utilization_pct}% used)\\nRemaining: ${matter_remaining}\\nLatest entry: {attorney_name} - {duration_hours}h (${entry_value})\\nAction: Discuss budget extension with client or scope reduction.",
      ),

      // 8. Weekly WIP summary (email)
      emailNode(
        "8",
        "Send weekly WIP summary",
        1720,
        "{billing_partner_email}",
        "Legal WIP Update: {matter_number} - {budget_status} ({budget_utilization_pct}%)",
        "Matter WIP Update\\n\\nMatter: {matter_number}\\nClient: {client_name}\\nBudget Status: {budget_status} ({budget_utilization_pct}% utilized)\\nTotal Spent: ${new_matter_spent}\\nRemaining: ${matter_remaining}\\n\\nLatest Entry:\\n- Attorney: {attorney_name}\\n- Activity: {improved_narrative}\\n- Hours: {duration_hours} @ ${hourly_rate}/hr = ${entry_value}\\n- UTBMS Code: {utbms_code}\\n\\nBilling Flags: {billing_flags}\\nWrite-down Risk: {write_down_risk}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // budget alert -> record + notify
      e("5", "6", "output_2"), // budget healthy -> just record
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 640. LOW-BALANCE OR TOP-UP REMINDER (7 nodes)
  // ===================================================================

  "Low-Balance or Top-Up Reminder": {
    summary:
      "Monitors account balances and sends proactive top-up reminders before depletion. A balance event or schedule triggers balance and usage rate pull, depletion projection runs via runCode, threshold is checked via condition, a top-up email with options is sent, critical balances get an SMS, and low-balance status is logged to Sheets.",
    nodes: [
      trigger("Webhook: Balance event or daily check", "webhook_app", {
        source: "Billing System / Balance API",
        event: "balance.low_threshold",
        howItStarts:
          "A balance drops below a configured threshold, or a daily scheduled check detects projected depletion based on usage patterns",
        setup:
          "Configure balance monitoring webhook or daily schedule. Include current balance, usage history, and account details.",
        inputFields: [],
      }),

      // 2. Balance + usage rate pull
      aiNode(
        "2",
        "AI: Extract balance & usage data",
        280,
        "You are a billing and usage analytics specialist. Parse the balance event to extract account details, current balance, recent usage rate, and account holder contact info. Respond JSON: {account_email: string, account_phone: string, account_name: string, account_id: string, current_balance: number, currency: string, avg_daily_usage: number, plan_name: string, top_up_url: string}",
        "Balance event payload: {webhook_payload}",
        [
          { field: "account_email", as: "account_email", type: "string" },
          { field: "account_phone", as: "account_phone", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "current_balance", as: "current_balance", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "avg_daily_usage", as: "avg_daily_usage", type: "number" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "top_up_url", as: "top_up_url", type: "string" },
        ],
      ),

      // 3. Depletion projection (runCode)
      n("3", "runCode", "Project depletion timeline", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Project when balance will be depleted based on usage rate
const balance = parseFloat(vars.current_balance) || 0;
const dailyUsage = parseFloat(vars.avg_daily_usage) || 1;
const daysRemaining = dailyUsage > 0 ? Math.floor(balance / dailyUsage) : 999;
const depletionDate = new Date();
depletionDate.setDate(depletionDate.getDate() + daysRemaining);
const urgency = daysRemaining <= 1 ? "critical" : daysRemaining <= 3 ? "high" : daysRemaining <= 7 ? "medium" : "low";
const suggestedTopUp = Math.ceil(dailyUsage * 30 * 100) / 100; // 30 days of usage
const isBelowThreshold = daysRemaining <= 7 ? "yes" : "no";
return {
  days_remaining: daysRemaining,
  depletion_date: depletionDate.toISOString().split("T")[0],
  urgency_level: urgency,
  suggested_top_up: suggestedTopUp.toFixed(2),
  is_below_threshold: isBelowThreshold,
  projection_summary: daysRemaining + " days of balance remaining at current usage ($" + dailyUsage.toFixed(2) + "/day)"
};`,
          outputVariables: [
            { name: "days_remaining", type: "number" },
            { name: "depletion_date", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "suggested_top_up", type: "string" },
            { name: "is_below_threshold", type: "string" },
            { name: "projection_summary", type: "string" },
          ],
        },
      }),

      // 4. Threshold condition
      n("4", "condition", "Below 7-day threshold?", 760, {
        conditionData: [
          {
            variable: "is_below_threshold",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Top-up email with options
      emailNode(
        "5",
        "Send top-up reminder",
        1000,
        "{account_email}",
        "Low Balance Alert: {days_remaining} Days Remaining on {plan_name}",
        "Hi {account_name},\\n\\nYour {plan_name} balance is running low.\\n\\nCurrent Balance: {currency} {current_balance}\\nAvg Daily Usage: {currency} {avg_daily_usage}\\nEstimated Depletion: {depletion_date} ({days_remaining} days)\\n\\nSuggested Top-Up: {currency} {suggested_top_up} (covers 30 days)\\n\\nTop Up Now: {top_up_url}\\n\\nTo avoid service interruption, please add funds before your balance is depleted.",
      ),

      // 6. Critical SMS
      smsNode(
        "6",
        "SMS: Critical balance alert",
        1240,
        "{account_phone}",
        "ALERT: Your {plan_name} balance ({currency} {current_balance}) will be depleted in {days_remaining} days. Top up now: {top_up_url}",
      ),

      // 7. Low-balance flagging + logging (Sheets)
      sheetsNode(
        "7",
        "Log low-balance event (Sheets)",
        1480,
        "LowBalanceLog",
        '[["{account_id}","{account_name}","{current_balance}","{currency}","{avg_daily_usage}","{days_remaining}","{depletion_date}","{urgency_level}","{suggested_top_up}","notified"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // below threshold -> email + SMS
      e("4", "7", "output_2"), // above threshold -> just log
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 641. MEMBERSHIP RENEWAL 30 DAYS OUT (7 nodes)
  // ===================================================================

  "Membership Renewal 30 Days Out": {
    summary:
      "Proactive 30-day renewal reminder with benefit recap and high-value CSM routing. A daily scan finds memberships expiring in 30 days, plan/benefits/usage data is pulled, AI generates a benefit recap, a renewal reminder email is sent, CRM renewal state is updated in HubSpot, high-value members are checked via condition, and CSM is alerted in Slack.",
    nodes: [
      trigger("Schedule: Daily 30-day renewal scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to identify memberships expiring in approximately 30 days and initiate proactive renewal outreach",
        setup:
          "Configure membership database query for 30-day expiry window. Set up HubSpot CRM for renewal tracking. Define high-value threshold for CSM routing.",
        inputFields: [],
      }),

      // 2. Plan/benefits/usage pull
      aiNode(
        "2",
        "AI: Pull member plan & usage data",
        280,
        "You are a membership retention specialist. Retrieve member details, their plan benefits, and recent usage metrics to build a compelling renewal case. Respond JSON: {member_email: string, member_name: string, member_id: string, plan_name: string, monthly_price: number, renewal_date: string, benefits_list: string, usage_highlights: string, total_value_received: number, is_high_value: boolean, csm_email: string, hubspot_contact_id: string}",
        "Membership renewal scan for 30-day window. Process pending renewals from the membership database.",
        [
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_name", as: "member_name", type: "string" },
          { field: "member_id", as: "member_id", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "monthly_price", as: "monthly_price", type: "number" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "benefits_list", as: "benefits_list", type: "string" },
          { field: "usage_highlights", as: "usage_highlights", type: "string" },
          {
            field: "total_value_received",
            as: "total_value_received",
            type: "number",
          },
          { field: "is_high_value", as: "is_high_value", type: "string" },
          { field: "csm_email", as: "csm_email", type: "string" },
          {
            field: "hubspot_contact_id",
            as: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 3. AI benefit recap
      aiNode(
        "3",
        "AI: Generate personalized benefit recap",
        520,
        "You are a retention copywriter specializing in membership renewals. Given the member's plan, usage data, and benefits, write a personalized benefit recap that highlights the value they've received and creates urgency around renewing. Respond JSON: {benefit_recap: string, value_proposition: string, renewal_cta: string, personal_touch: string}",
        "Member: {member_name}. Plan: {plan_name} at ${monthly_price}/mo. Benefits: {benefits_list}. Usage: {usage_highlights}. Total value received: ${total_value_received}. Renewal date: {renewal_date}.",
        [
          { field: "benefit_recap", as: "benefit_recap", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "renewal_cta", as: "renewal_cta", type: "string" },
          { field: "personal_touch", as: "personal_touch", type: "string" },
        ],
      ),

      // 4. Renewal reminder email
      emailNode(
        "4",
        "Send 30-day renewal reminder",
        760,
        "{member_email}",
        "Your {plan_name} Membership Renews in 30 Days",
        "Hi {member_name},\\n\\n{personal_touch}\\n\\nYour {plan_name} membership renews on {renewal_date}.\\n\\n{benefit_recap}\\n\\nValue Highlights:\\n{value_proposition}\\n\\nYour Usage:\\n{usage_highlights}\\n\\n{renewal_cta}\\n\\nQuestions? Reply to this email and we will be happy to help.",
      ),

      // 5. CRM renewal state update (HubSpot)
      hubspotNode(
        "5",
        "Update CRM renewal state (HubSpot)",
        1000,
        "PATCH",
        "crm/v3/objects/contacts/{hubspot_contact_id}",
        [
          {
            key: "properties",
            value:
              '{"renewal_status":"30_day_reminder_sent","renewal_date":"{renewal_date}","last_renewal_touch":"30_day_email"}',
          },
        ],
        [{ name: "id", customName: "hubspot_update_id", type: "string" }],
      ),

      // 6. High-value CSM condition
      n("6", "condition", "High-value member?", 1240, {
        conditionData: [
          {
            variable: "is_high_value",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. CSM Slack alert
      slackNode(
        "7",
        "Slack: CSM high-value renewal alert",
        1480,
        ":star: High-Value Renewal Alert (30 Days)\\nMember: {member_name} ({member_email})\\nPlan: {plan_name} (${monthly_price}/mo)\\nRenewal: {renewal_date}\\nValue Received: ${total_value_received}\\nAction: Schedule personal renewal call within 1 week.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // high value -> CSM alert
      e("6", "7", "output_2"), // standard -> still log for visibility
    ],
  },

  // ===================================================================
  // 642. MEMBERSHIP RENEWAL 7 DAYS OUT (7 nodes)
  // ===================================================================

  "Membership Renewal 7 Days Out": {
    summary:
      "Urgent 7-day renewal reminder with payment method validation and SMS follow-up. A daily scan finds 7-day expirations, prior reminder engagement is checked, payment method is validated via condition, an urgent countdown email is sent, SMS reminder follows, payment update flow triggers if needed, and a retention alert goes to Slack.",
    nodes: [
      trigger("Schedule: Daily 7-day renewal scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to identify memberships expiring within 7 days and send urgent renewal reminders with payment validation",
        setup:
          "Configure membership database for 7-day expiry window. Ensure payment method status is accessible via Stripe customer API. Set up SMS for urgent reminders.",
        inputFields: [],
      }),

      // 2. Prior reminder engagement check
      aiNode(
        "2",
        "AI: Check engagement & member data",
        280,
        "You are a membership retention analyst. For the 7-day renewal cohort, pull member data and check whether they engaged with the 30-day reminder (opened email, clicked CTA). Also verify payment method status. Respond JSON: {member_email: string, member_phone: string, member_name: string, plan_name: string, renewal_date: string, opened_30day_email: boolean, clicked_30day_cta: boolean, payment_method_valid: boolean, last_four: string, card_expiry: string, monthly_price: number, member_since: string}",
        "7-day renewal scan. Pull members with renewal dates within 7 days and their engagement history.",
        [
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_phone", as: "member_phone", type: "string" },
          { field: "member_name", as: "member_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          {
            field: "opened_30day_email",
            as: "opened_30day_email",
            type: "string",
          },
          {
            field: "clicked_30day_cta",
            as: "clicked_30day_cta",
            type: "string",
          },
          {
            field: "payment_method_valid",
            as: "payment_method_valid",
            type: "string",
          },
          { field: "last_four", as: "last_four", type: "string" },
          { field: "card_expiry", as: "card_expiry", type: "string" },
          { field: "monthly_price", as: "monthly_price", type: "number" },
          { field: "member_since", as: "member_since", type: "string" },
        ],
      ),

      // 3. Payment method condition
      n("3", "condition", "Payment method valid?", 520, {
        conditionData: [
          {
            variable: "payment_method_valid",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Urgent countdown email
      emailNode(
        "4",
        "Send 7-day countdown email",
        760,
        "{member_email}",
        "7 Days Left: Your {plan_name} Membership Expires {renewal_date}",
        "Hi {member_name},\\n\\nYour {plan_name} membership expires in just 7 days on {renewal_date}.\\n\\nYou've been a valued member since {member_since}. Don't let your membership lapse!\\n\\nPayment: Card ending {last_four} (exp: {card_expiry})\\nAmount: ${monthly_price}/month\\n\\nYour membership will automatically renew if your payment method is up to date. No action needed if everything looks correct.\\n\\nNeed to update your payment? Visit your account settings.",
      ),

      // 5. SMS reminder
      smsNode(
        "5",
        "SMS: Urgent renewal reminder",
        1000,
        "{member_phone}",
        "Hi {member_name}, your {plan_name} membership expires in 7 days ({renewal_date}). Ensure your payment method is current to avoid interruption. Questions? Reply HELP.",
      ),

      // 6. Payment update flow
      emailNode(
        "6",
        "Send payment update request",
        1240,
        "{member_email}",
        "Action Required: Update Payment Method Before {renewal_date}",
        "Hi {member_name},\\n\\nWe noticed your payment method on file may need updating before your {plan_name} membership renews on {renewal_date}.\\n\\nYour current card (ending {last_four}, exp: {card_expiry}) may not process successfully.\\n\\nPlease update your payment method to ensure uninterrupted access:\\nhttps://account.example.com/billing\\n\\nIf you've already updated, please disregard this message.",
      ),

      // 7. Retention Slack alert
      slackNode(
        "7",
        "Slack: 7-day retention alert",
        1480,
        ":hourglass: 7-Day Renewal Alert\\nMember: {member_name} ({member_email})\\nPlan: {plan_name} (${monthly_price}/mo) | Since: {member_since}\\nRenewal: {renewal_date}\\nPayment Valid: {payment_method_valid} | Card: ****{last_four}\\n30-day email opened: {opened_30day_email} | CTA clicked: {clicked_30day_cta}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // payment valid -> standard countdown
      e("3", "6", "output_2"), // payment invalid -> update flow first
      e("4", "5"),
      e("5", "7"),
      e("6", "5"),
    ],
  },

  // ===================================================================
  // 643. MEMBERSHIP-EXPIRED REACTIVATION (8 nodes)
  // ===================================================================

  "Membership-Expired Reactivation": {
    summary:
      "Multi-touch reactivation campaign for lapsed memberships. A membership lapse trigger builds the member profile, AI selects a reactivation offer, a day-1 'we miss you' email is sent, a day-7 social proof email follows, a final offer email/SMS is sent, reactivation conversion is checked via condition, and ROI is logged to Sheets.",
    nodes: [
      trigger("Webhook: Membership expired", "webhook_app", {
        source: "Membership System",
        event: "membership.expired",
        howItStarts:
          "A membership lapses (not renewed by expiry date), triggering a multi-stage reactivation campaign to win the member back",
        setup:
          "Configure membership system webhook for expiration events. Include full member history, usage data, and churn reason if available.",
        inputFields: [],
      }),

      // 2. Member profile build
      aiNode(
        "2",
        "AI: Build lapsed member profile",
        280,
        "You are a membership retention analyst. Parse the lapsed member data to build a comprehensive profile for reactivation targeting. Assess churn risk factors and potential reactivation likelihood. Respond JSON: {member_email: string, member_phone: string, member_name: string, plan_name: string, monthly_price: number, member_tenure_months: number, total_lifetime_value: number, last_usage_date: string, churn_reason: string, reactivation_likelihood: string, preferred_channel: string}",
        "Membership expiration payload: {webhook_payload}",
        [
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_phone", as: "member_phone", type: "string" },
          { field: "member_name", as: "member_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "monthly_price", as: "monthly_price", type: "number" },
          {
            field: "member_tenure_months",
            as: "member_tenure_months",
            type: "number",
          },
          {
            field: "total_lifetime_value",
            as: "total_lifetime_value",
            type: "number",
          },
          { field: "last_usage_date", as: "last_usage_date", type: "string" },
          { field: "churn_reason", as: "churn_reason", type: "string" },
          {
            field: "reactivation_likelihood",
            as: "reactivation_likelihood",
            type: "string",
          },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
        ],
      ),

      // 3. AI reactivation offer selection
      aiNode(
        "3",
        "AI: Select reactivation offer",
        520,
        "You are a retention offer strategist. Based on the member's profile, tenure, lifetime value, and churn reason, select the optimal reactivation offer. Options range from discount (10-30%), free month, plan upgrade trial, to feature unlock. Respond JSON: {offer_type: string, offer_details: string, discount_pct: number, offer_expiry_days: number, reactivation_url: string, win_back_message: string, social_proof_stat: string}",
        "Member: {member_name}. Plan: {plan_name} (${monthly_price}/mo). Tenure: {member_tenure_months} months. LTV: ${total_lifetime_value}. Churn reason: {churn_reason}. Reactivation likelihood: {reactivation_likelihood}.",
        [
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "offer_details", as: "offer_details", type: "string" },
          { field: "discount_pct", as: "discount_pct", type: "number" },
          {
            field: "offer_expiry_days",
            as: "offer_expiry_days",
            type: "number",
          },
          { field: "reactivation_url", as: "reactivation_url", type: "string" },
          { field: "win_back_message", as: "win_back_message", type: "string" },
          {
            field: "social_proof_stat",
            as: "social_proof_stat",
            type: "string",
          },
        ],
      ),

      // 4. Day-1 "we miss you" email
      emailNode(
        "4",
        "Day 1: We miss you email",
        760,
        "{member_email}",
        "We Miss You, {member_name}!",
        "Hi {member_name},\\n\\n{win_back_message}\\n\\nWe noticed your {plan_name} membership has expired. During your {member_tenure_months} months with us, you were a valued member.\\n\\nWe'd love to have you back. As a special welcome-back offer:\\n\\n{offer_details}\\n\\nReactivate Now: {reactivation_url}\\n\\nThis offer is available for {offer_expiry_days} days.",
      ),

      // 5. Day-7 social proof email (after delay)
      n("5", "delayAction", "Wait 7 days", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "7",
          delayUnit: "days",
        },
      }),

      // 6. Final offer email/SMS
      emailNode(
        "6",
        "Day 7: Social proof + final offer",
        1240,
        "{member_email}",
        "Members Like You Are Loving These Updates",
        "Hi {member_name},\\n\\nSince you've been away, we've been busy making things even better.\\n\\n{social_proof_stat}\\n\\nYour special reactivation offer is still available:\\n{offer_details}\\n\\nDon't miss out - this offer expires soon.\\n\\nReactivate: {reactivation_url}",
      ),

      // 7. Reactivation conversion check condition
      n("7", "condition", "Member reactivated?", 1480, {
        conditionData: [
          {
            variable: "reactivation_likelihood",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. ROI logging (Sheets)
      sheetsNode(
        "8",
        "Log reactivation campaign ROI (Sheets)",
        1720,
        "ReactivationROI",
        '[["{member_email}","{member_name}","{plan_name}","{monthly_price}","{member_tenure_months}","{total_lifetime_value}","{churn_reason}","{offer_type}","{discount_pct}","{reactivation_likelihood}","campaign_complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // reactivated -> log success
      e("7", "8", "output_2"), // not reactivated -> log for review
    ],
  },

  // ===================================================================
  // 644. MONTHLY CLOSE CHECKLIST (8 nodes)
  // ===================================================================

  "Monthly Close Checklist": {
    summary:
      "Orchestrates the monthly financial close process with checklist tracking and reminders. A first-business-day trigger instantiates a checklist from template via runCode, owner/due-date assignment runs, recurring tasks are auto-populated, completion is monitored, overdue reminders go to Slack, a close dashboard is written to Sheets, and daily status updates are emailed.",
    nodes: [
      trigger("Schedule: First business day of month", "schedule", {
        schedule: "0 7 1 * *",
        howItStarts:
          "Triggers on the first day of each month at 7 AM to initiate the monthly financial close process with a structured checklist",
        setup:
          "Configure close checklist template with standard tasks (bank rec, revenue recognition, AP/AR close, journal entries, etc.). Set up owner assignments and due-date offsets. Create Sheets dashboard template.",
        inputFields: [],
      }),

      // 2. Checklist instantiation from template (runCode)
      n("2", "runCode", "Instantiate close checklist", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Instantiate monthly close checklist from template
const now = new Date();
const month = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
const closeDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of month
const template = [
  { task: "Bank reconciliation", owner: "treasury", dueDayOffset: 2, category: "cash" },
  { task: "Revenue recognition entries", owner: "revenue_accountant", dueDayOffset: 3, category: "revenue" },
  { task: "AP subledger close", owner: "ap_team", dueDayOffset: 3, category: "payables" },
  { task: "AR subledger close", owner: "ar_team", dueDayOffset: 3, category: "receivables" },
  { task: "Payroll journal entries", owner: "payroll", dueDayOffset: 4, category: "payroll" },
  { task: "Depreciation & amortization", owner: "fixed_assets", dueDayOffset: 5, category: "assets" },
  { task: "Intercompany eliminations", owner: "consolidation", dueDayOffset: 6, category: "consolidation" },
  { task: "Accruals & prepayments", owner: "general_ledger", dueDayOffset: 5, category: "accruals" },
  { task: "Trial balance review", owner: "controller", dueDayOffset: 7, category: "review" },
  { task: "Management reporting package", owner: "fpa", dueDayOffset: 8, category: "reporting" },
];
const tasks = template.map((t, i) => {
  const dueDate = new Date(now);
  dueDate.setDate(dueDate.getDate() + t.dueDayOffset);
  return { id: "CLOSE-" + (i + 1), ...t, dueDate: dueDate.toISOString().split("T")[0], status: "pending" };
});
return {
  close_month: month,
  total_tasks: tasks.length,
  checklist_json: JSON.stringify(tasks),
  target_close_date: closeDate.toISOString().split("T")[0],
  categories: "cash, revenue, payables, receivables, payroll, assets, consolidation, accruals, review, reporting",
  checklist_summary: tasks.length + " close tasks instantiated for " + month
};`,
          outputVariables: [
            { name: "close_month", type: "string" },
            { name: "total_tasks", type: "number" },
            { name: "checklist_json", type: "string" },
            { name: "target_close_date", type: "string" },
            { name: "categories", type: "string" },
            { name: "checklist_summary", type: "string" },
          ],
        },
      }),

      // 3. Owner/due-date assignment (AI)
      aiNode(
        "3",
        "AI: Assign owners & optimize schedule",
        520,
        "You are a monthly close process manager. Given the checklist with default owners and due dates, optimize the schedule for parallel execution where possible, identify critical path items, and flag any dependency risks. Respond JSON: {optimized_schedule: string, critical_path: string, parallel_tasks: string, dependency_risks: string, estimated_close_days: number}",
        "Close month: {close_month}. Target close: {target_close_date}. Tasks: {checklist_json}. Categories: {categories}.",
        [
          {
            field: "optimized_schedule",
            as: "optimized_schedule",
            type: "string",
          },
          { field: "critical_path", as: "critical_path", type: "string" },
          { field: "parallel_tasks", as: "parallel_tasks", type: "string" },
          { field: "dependency_risks", as: "dependency_risks", type: "string" },
          {
            field: "estimated_close_days",
            as: "estimated_close_days",
            type: "number",
          },
        ],
      ),

      // 4. Recurring task auto-population
      aiNode(
        "4",
        "AI: Populate recurring task details",
        760,
        "You are an accounting automation specialist. For each close task, populate specific sub-steps, required data sources, and completion criteria. This ensures team members know exactly what to do. Respond JSON: {task_details: string, data_sources_needed: string, completion_criteria: string}",
        "Close checklist: {checklist_json}. Optimized schedule: {optimized_schedule}.",
        [
          { field: "task_details", as: "task_details", type: "string" },
          {
            field: "data_sources_needed",
            as: "data_sources_needed",
            type: "string",
          },
          {
            field: "completion_criteria",
            as: "completion_criteria",
            type: "string",
          },
        ],
      ),

      // 5. Completion monitoring
      n("5", "condition", "All tasks on track?", 1000, {
        conditionData: [
          {
            variable: "estimated_close_days",
            value: "10",
            comparison_operator: "LESS THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Overdue reminders (Slack)
      slackNode(
        "6",
        "Slack: Close task reminders",
        1240,
        ":calendar: Monthly Close Kickoff: {close_month}\\nTotal Tasks: {total_tasks} | Target Close: {target_close_date}\\nEst. Days: {estimated_close_days}\\nCritical Path: {critical_path}\\nDependency Risks: {dependency_risks}\\nAll task owners have been notified. Track progress in the Close Dashboard.",
      ),

      // 7. Close dashboard (Sheets)
      sheetsNode(
        "7",
        "Write close dashboard (Sheets)",
        1480,
        "MonthlyClose",
        '[["{close_month}","{total_tasks}","{target_close_date}","{estimated_close_days}","{critical_path}","{parallel_tasks}","{dependency_risks}","in_progress"]]',
      ),

      // 8. Daily status update (email)
      emailNode(
        "8",
        "Send close status update",
        1720,
        "{controller_email}",
        "Monthly Close Initiated: {close_month} - {total_tasks} Tasks",
        "Monthly Close Process Initiated\\n\\nMonth: {close_month}\\nTarget Close Date: {target_close_date}\\nTotal Tasks: {total_tasks}\\nEstimated Days to Close: {estimated_close_days}\\n\\nCritical Path:\\n{critical_path}\\n\\nParallel Tasks:\\n{parallel_tasks}\\n\\nDependency Risks:\\n{dependency_risks}\\n\\nTask Details:\\n{task_details}\\n\\nData Sources Needed:\\n{data_sources_needed}\\n\\nCompletion Criteria:\\n{completion_criteria}\\n\\nTrack progress in the Monthly Close Google Sheet dashboard.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // on track -> standard notifications
      e("5", "6", "output_2"), // behind -> urgent notifications
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 645. MONTHLY FINANCIAL CLOSE CHECKLIST (9 nodes)
  // ===================================================================

  "Monthly Financial Close Checklist": {
    summary:
      "Comprehensive month-end financial close with Jira task creation, automation, reconciliation, AI review, and approval gates. A month-end trigger creates Jira tasks, bank rec runs automatically, revenue recognition schedules are computed via runCode, AP/AR subledger reconciliation runs, AI reviews journal entries, a manager approval gate fires, close progress is tracked in Sheets, and leadership receives a completion notification.",
    nodes: [
      trigger("Schedule: Month-end financial close", "schedule", {
        schedule: "0 6 28-31 * *",
        howItStarts:
          "Triggers on the last business day of each month to initiate the comprehensive financial close process with automated task management",
        setup:
          "Configure Jira project for close tasks. Set up bank API for automated reconciliation. Prepare revenue recognition rules and subledger reconciliation templates. Define approval workflows.",
        inputFields: [],
      }),

      // 2. Jira task creation
      n("2", "hitApi", "Create Jira close tasks", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue/bulk",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "issueUpdates",
              value:
                '[{"fields":{"project":{"key":"{jira_project}"},"summary":"Month-End Close: Bank Reconciliation","issuetype":{"name":"Task"},"assignee":{"accountId":"{treasury_jira_id}"},"priority":{"name":"High"}}},{"fields":{"project":{"key":"{jira_project}"},"summary":"Month-End Close: Revenue Recognition","issuetype":{"name":"Task"},"assignee":{"accountId":"{revenue_jira_id}"},"priority":{"name":"High"}}},{"fields":{"project":{"key":"{jira_project}"},"summary":"Month-End Close: AP/AR Reconciliation","issuetype":{"name":"Task"},"assignee":{"accountId":"{ap_ar_jira_id}"},"priority":{"name":"High"}}}]',
            },
          ],
          variables: [
            {
              name: "issues",
              customName: "jira_issues_created",
              type: "string",
            },
            { name: "errors", customName: "jira_errors", type: "string" },
          ],
        },
      }),

      // 3. Bank rec automation
      aiNode(
        "3",
        "AI: Automated bank reconciliation",
        520,
        "You are a bank reconciliation specialist. Simulate pulling bank statements and GL cash entries to perform an automated reconciliation. Identify matched transactions, outstanding checks, deposits in transit, and unexplained differences. Respond JSON: {bank_balance: number, gl_balance: number, matched_count: number, outstanding_checks: number, deposits_in_transit: number, unexplained_diff: number, rec_status: string, rec_items: string}",
        "Run automated bank reconciliation for the current month-end close period.",
        [
          { field: "bank_balance", as: "bank_balance", type: "number" },
          { field: "gl_balance", as: "gl_balance", type: "number" },
          {
            field: "matched_count",
            as: "matched_transactions",
            type: "number",
          },
          {
            field: "outstanding_checks",
            as: "outstanding_checks",
            type: "number",
          },
          {
            field: "deposits_in_transit",
            as: "deposits_in_transit",
            type: "number",
          },
          {
            field: "unexplained_diff",
            as: "unexplained_difference",
            type: "number",
          },
          { field: "rec_status", as: "bank_rec_status", type: "string" },
          { field: "rec_items", as: "rec_items_detail", type: "string" },
        ],
      ),

      // 4. Revenue recognition schedules (runCode)
      n("4", "runCode", "Compute revenue recognition schedules", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute revenue recognition for the month
const now = new Date();
const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
const daysInMonth = monthEnd.getDate();
// Simulated deferred revenue contracts
const contracts = [
  { id: "C001", total: 120000, term_months: 12, start: "2025-07-01" },
  { id: "C002", total: 60000, term_months: 6, start: "2025-10-01" },
  { id: "C003", total: 36000, term_months: 12, start: "2025-01-01" },
];
let totalRecognized = 0;
const schedules = contracts.map(c => {
  const monthlyRev = c.total / c.term_months;
  const startDate = new Date(c.start);
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + c.term_months);
  const isActive = monthStart >= startDate && monthStart < endDate;
  const recognized = isActive ? monthlyRev : 0;
  totalRecognized += recognized;
  return { contract_id: c.id, monthly_amount: monthlyRev.toFixed(2), recognized: recognized.toFixed(2), active: isActive };
});
return {
  total_recognized_revenue: totalRecognized.toFixed(2),
  contracts_processed: contracts.length,
  active_contracts: schedules.filter(s => s.active).length,
  recognition_schedules: JSON.stringify(schedules),
  rev_rec_summary: "$" + totalRecognized.toFixed(2) + " recognized across " + schedules.filter(s => s.active).length + " active contracts"
};`,
          outputVariables: [
            { name: "total_recognized_revenue", type: "string" },
            { name: "contracts_processed", type: "number" },
            { name: "active_contracts", type: "number" },
            { name: "recognition_schedules", type: "string" },
            { name: "rev_rec_summary", type: "string" },
          ],
        },
      }),

      // 5. AP/AR subledger reconciliation
      aiNode(
        "5",
        "AI: Reconcile AP/AR subledgers",
        1000,
        "You are a subledger reconciliation specialist. Perform AP and AR subledger-to-GL reconciliation. Identify any out-of-balance conditions, unreconciled items, and required adjusting entries. Respond JSON: {ap_subledger_total: number, ap_gl_total: number, ap_difference: number, ar_subledger_total: number, ar_gl_total: number, ar_difference: number, adjusting_entries_needed: number, reconciliation_status: string, ap_ar_summary: string}",
        "Month-end subledger reconciliation. Bank rec status: {bank_rec_status}. Revenue recognized: {rev_rec_summary}.",
        [
          {
            field: "ap_subledger_total",
            as: "ap_subledger_total",
            type: "number",
          },
          { field: "ap_gl_total", as: "ap_gl_total", type: "number" },
          { field: "ap_difference", as: "ap_difference", type: "number" },
          {
            field: "ar_subledger_total",
            as: "ar_subledger_total",
            type: "number",
          },
          { field: "ar_gl_total", as: "ar_gl_total", type: "number" },
          { field: "ar_difference", as: "ar_difference", type: "number" },
          {
            field: "adjusting_entries_needed",
            as: "adjusting_entries_needed",
            type: "number",
          },
          {
            field: "reconciliation_status",
            as: "subledger_rec_status",
            type: "string",
          },
          { field: "ap_ar_summary", as: "ap_ar_summary", type: "string" },
        ],
      ),

      // 6. AI journal entry review
      aiNode(
        "6",
        "AI: Review journal entries",
        1240,
        "You are a senior accountant reviewing month-end journal entries. Check all entries for proper coding, supporting documentation references, and policy compliance. Flag any unusual entries, reclassifications, or entries exceeding materiality thresholds. Respond JSON: {entries_reviewed: number, flagged_entries: number, flags_detail: string, approval_recommendation: string, review_notes: string}",
        "Bank rec: {bank_rec_status} (diff: ${unexplained_difference}). Rev rec: {rev_rec_summary}. AP/AR: {ap_ar_summary}. Adjusting entries: {adjusting_entries_needed}.",
        [
          { field: "entries_reviewed", as: "entries_reviewed", type: "number" },
          { field: "flagged_entries", as: "flagged_entries", type: "number" },
          { field: "flags_detail", as: "flags_detail", type: "string" },
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
          { field: "review_notes", as: "review_notes", type: "string" },
        ],
      ),

      // 7. Manager approval gate
      n("7", "approvalGate", "Manager approval for close", 1480, {
        approvalGateDetails: {
          approverEmail: "{controller_email}",
          approvalMessage:
            "Monthly Close Approval Required\\n\\nBank Rec: {bank_rec_status}\\nRev Rec: {rev_rec_summary}\\nAP/AR: {ap_ar_summary}\\nEntries Reviewed: {entries_reviewed} (Flagged: {flagged_entries})\\nRecommendation: {approval_recommendation}\\n\\nReview Notes: {review_notes}",
        },
      }),

      // 8. Close progress tracking (Sheets)
      sheetsNode(
        "8",
        "Track close progress (Sheets)",
        1720,
        "FinancialClose",
        '[["{bank_rec_status}","{total_recognized_revenue}","{ap_ar_summary}","{entries_reviewed}","{flagged_entries}","{approval_recommendation}","{subledger_rec_status}","complete"]]',
      ),

      // 9. Leadership completion notification
      emailNode(
        "9",
        "Notify leadership of close completion",
        1960,
        "{cfo_email}",
        "Monthly Financial Close Complete",
        "Monthly Financial Close Summary\\n\\nBank Reconciliation: {bank_rec_status}\\n- Matched: {matched_transactions} transactions\\n- Outstanding checks: {outstanding_checks}\\n- Unexplained difference: ${unexplained_difference}\\n\\nRevenue Recognition: {rev_rec_summary}\\n\\nAP/AR Reconciliation: {ap_ar_summary}\\n- Adjusting entries: {adjusting_entries_needed}\\n\\nJournal Entry Review:\\n- Entries reviewed: {entries_reviewed}\\n- Flagged: {flagged_entries}\\n- Notes: {review_notes}\\n\\nApproval Status: {approval_recommendation}\\n\\nFull details available in the Financial Close Google Sheet.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 646. MULTI CURRENCY PRICE CONVERTER BOT (7 nodes)
  // ===================================================================

  "Multi Currency Price Converter Bot": {
    summary:
      "Real-time currency conversion bot with trend context and target-rate alerts. A Slack/portal trigger receives conversion requests, real-time FX rates are fetched, conversion is calculated via runCode, single vs multi-pair routing runs via condition, AI provides 30-day trend context, conversion results are delivered via Slack, and target-rate alerts are created.",
    nodes: [
      trigger("Webhook: Currency conversion requested", "webhook_app", {
        source: "Slack / Internal Portal",
        event: "fx.conversion.requested",
        howItStarts:
          "A user requests a currency conversion via Slack command (/convert) or the internal portal, triggering real-time rate lookup and conversion",
        setup:
          "Configure Slack slash command (/convert 100 USD to EUR) or portal form webhook. Set up exchange rate API (ExchangeRate-API, Open Exchange Rates, or similar).",
        inputFields: [],
      }),

      // 2. Real-time FX rate fetch
      aiNode(
        "2",
        "AI: Parse request & fetch FX rates",
        280,
        "You are a foreign exchange specialist. Parse the conversion request to identify the source currency, target currency(ies), and amount. If the request includes multiple target currencies, handle all pairs. Respond JSON: {requester_email: string, requester_name: string, source_currency: string, target_currencies: string, amount: number, is_multi_pair: boolean, request_text: string}",
        "Conversion request payload: {webhook_payload}",
        [
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "source_currency", as: "source_currency", type: "string" },
          {
            field: "target_currencies",
            as: "target_currencies",
            type: "string",
          },
          { field: "amount", as: "convert_amount", type: "number" },
          { field: "is_multi_pair", as: "is_multi_pair", type: "string" },
          { field: "request_text", as: "request_text", type: "string" },
        ],
      ),

      // 3. Conversion calculation (runCode)
      n("3", "runCode", "Calculate conversions", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate currency conversions using fetched rates
const amount = parseFloat(vars.convert_amount) || 0;
const source = vars.source_currency || "USD";
const targets = (vars.target_currencies || "EUR").split(",").map(t => t.trim());
const isMulti = targets.length > 1 ? "yes" : "no";
// In production, rates come from the FX API call
// Using representative rates for calculation framework
const ratesFromUSD = { EUR: 0.92, GBP: 0.79, JPY: 149.50, CAD: 1.36, AUD: 1.53, CHF: 0.88, CNY: 7.24, INR: 83.12, MXN: 17.15, BRL: 4.97 };
const sourceRate = source === "USD" ? 1 : (ratesFromUSD[source] || 1);
const amountInUSD = amount / sourceRate;
const conversions = targets.map(t => {
  const targetRate = t === "USD" ? 1 : (ratesFromUSD[t] || 1);
  const converted = amountInUSD * targetRate;
  const directRate = targetRate / sourceRate;
  return { target: t, rate: directRate.toFixed(6), converted: converted.toFixed(2) };
});
const resultText = conversions.map(c => amount.toFixed(2) + " " + source + " = " + c.converted + " " + c.target + " (rate: " + c.rate + ")").join("\\n");
return {
  conversion_results: JSON.stringify(conversions),
  result_text: resultText,
  primary_converted: conversions[0] ? conversions[0].converted : "0",
  primary_rate: conversions[0] ? conversions[0].rate : "0",
  primary_target: conversions[0] ? conversions[0].target : targets[0],
  pair_count: conversions.length,
  is_multi_conversion: isMulti
};`,
          outputVariables: [
            { name: "conversion_results", type: "string" },
            { name: "result_text", type: "string" },
            { name: "primary_converted", type: "string" },
            { name: "primary_rate", type: "string" },
            { name: "primary_target", type: "string" },
            { name: "pair_count", type: "number" },
            { name: "is_multi_conversion", type: "string" },
          ],
        },
      }),

      // 4. Single vs multi-pair condition
      n("4", "condition", "Multi-pair conversion?", 760, {
        conditionData: [
          {
            variable: "is_multi_conversion",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI 30-day trend context
      aiNode(
        "5",
        "AI: 30-day trend analysis",
        1000,
        "You are a forex market analyst. Given the currency pair(s) and current rate(s), provide 30-day trend context including direction (strengthening/weakening), volatility level, and any notable events affecting the pair. Respond JSON: {trend_summary: string, direction: string, volatility: string, notable_events: string, rate_forecast: string}",
        "Pair: {source_currency}/{primary_target}. Current rate: {primary_rate}. Amount: {convert_amount} {source_currency}. Conversions: {conversion_results}.",
        [
          { field: "trend_summary", as: "trend_summary", type: "string" },
          { field: "direction", as: "trend_direction", type: "string" },
          { field: "volatility", as: "volatility_level", type: "string" },
          { field: "notable_events", as: "notable_events", type: "string" },
          { field: "rate_forecast", as: "rate_forecast", type: "string" },
        ],
      ),

      // 6. Conversion result delivery (Slack)
      slackNode(
        "6",
        "Slack: Deliver conversion results",
        1240,
        ":currency_exchange: Currency Conversion Result\\n{result_text}\\n\\n30-Day Trend ({source_currency}/{primary_target}): {trend_direction}\\n{trend_summary}\\nVolatility: {volatility_level}\\nForecast: {rate_forecast}",
      ),

      // 7. Target-rate alert creation
      sheetsNode(
        "7",
        "Create target-rate alert (Sheets)",
        1480,
        "FXAlerts",
        '[["{requester_email}","{source_currency}","{primary_target}","{primary_rate}","{convert_amount}","{trend_direction}","{volatility_level}","active"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // multi-pair -> full trend analysis
      e("4", "5", "output_2"), // single pair -> standard trend
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 647. MULTI CURRENCY TRANSACTION TRACKER (9 nodes)
  // ===================================================================

  "Multi Currency Transaction Tracker": {
    summary:
      "Tracks non-base-currency transactions with FX enrichment, anomaly screening, and exposure dashboards. A non-base-currency payment trigger enriches with FX rates, spread/gain-loss is computed via runCode, AI screens for anomalies, risk score is checked via condition, fraud review escalates in Slack, an exposure dashboard is written to Sheets, a treasury summary is emailed, and an audit log records the transaction.",
    nodes: [
      trigger("Webhook: Non-base-currency payment", "webhook_app", {
        source: "Stripe / Payment Processor",
        event: "charge.succeeded",
        howItStarts:
          "A payment is processed in a non-base currency, triggering FX enrichment, gain/loss computation, and exposure tracking",
        setup:
          "Configure Stripe webhook to filter for charges where currency differs from base currency. Include charge details and customer info. Set up FX rate API for mid-market rates.",
        inputFields: [],
      }),

      // 2. FX rate enrichment
      aiNode(
        "2",
        "AI: Extract payment & enrich FX data",
        280,
        "You are a treasury operations specialist. Parse the payment webhook to extract transaction details and enrich with mid-market FX rate data. Identify the transaction currency, base currency equivalent, and the rate used by the payment processor. Respond JSON: {transaction_id: string, customer_email: string, transaction_currency: string, base_currency: string, transaction_amount: number, processor_rate: number, mid_market_rate: number, base_amount: number, customer_country: string, payment_method: string, timestamp: string}",
        "Non-base-currency payment payload: {webhook_payload}",
        [
          { field: "transaction_id", as: "transaction_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          {
            field: "transaction_currency",
            as: "transaction_currency",
            type: "string",
          },
          { field: "base_currency", as: "base_currency", type: "string" },
          {
            field: "transaction_amount",
            as: "transaction_amount",
            type: "number",
          },
          { field: "processor_rate", as: "processor_rate", type: "number" },
          { field: "mid_market_rate", as: "mid_market_rate", type: "number" },
          { field: "base_amount", as: "base_amount", type: "number" },
          { field: "customer_country", as: "customer_country", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "timestamp", as: "txn_timestamp", type: "string" },
        ],
      ),

      // 3. Spread/gain-loss computation (runCode)
      n("3", "runCode", "Compute FX spread & gain/loss", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute FX spread and unrealized gain/loss
const txnAmount = parseFloat(vars.transaction_amount) || 0;
const processorRate = parseFloat(vars.processor_rate) || 1;
const midMarketRate = parseFloat(vars.mid_market_rate) || 1;
const baseAmount = parseFloat(vars.base_amount) || 0;
// Spread calculation (what processor charges vs mid-market)
const fairValueBase = txnAmount * midMarketRate;
const spread = Math.abs(processorRate - midMarketRate);
const spreadPct = midMarketRate > 0 ? ((spread / midMarketRate) * 100) : 0;
const spreadCost = Math.abs(baseAmount - fairValueBase);
// Gain/loss (positive = favorable for us)
const fxGainLoss = fairValueBase - baseAmount;
const isGain = fxGainLoss >= 0;
// Risk scoring
const riskFactors = [];
if (spreadPct > 3) riskFactors.push("high_spread");
if (txnAmount > 50000) riskFactors.push("large_transaction");
if (Math.abs(fxGainLoss) > 1000) riskFactors.push("material_fx_impact");
const riskScore = riskFactors.length * 30 + (spreadPct > 2 ? 20 : 0);
const riskLevel = riskScore >= 80 ? "high" : riskScore >= 40 ? "medium" : "low";
return {
  spread_pct: spreadPct.toFixed(3),
  spread_cost_base: spreadCost.toFixed(2),
  fx_gain_loss: fxGainLoss.toFixed(2),
  is_fx_gain: isGain ? "yes" : "no",
  fair_value_base: fairValueBase.toFixed(2),
  risk_score: riskScore,
  risk_level: riskLevel,
  risk_factors: riskFactors.join(", ") || "none",
  fx_summary: (isGain ? "Gain" : "Loss") + " of $" + Math.abs(fxGainLoss).toFixed(2) + " | Spread: " + spreadPct.toFixed(3) + "% ($" + spreadCost.toFixed(2) + ")"
};`,
          outputVariables: [
            { name: "spread_pct", type: "string" },
            { name: "spread_cost_base", type: "string" },
            { name: "fx_gain_loss", type: "string" },
            { name: "is_fx_gain", type: "string" },
            { name: "fair_value_base", type: "string" },
            { name: "risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "risk_factors", type: "string" },
            { name: "fx_summary", type: "string" },
          ],
        },
      }),

      // 4. AI anomaly screening
      aiNode(
        "4",
        "AI: Screen for FX anomalies",
        760,
        "You are a treasury risk analyst specializing in FX fraud detection and anomaly screening. Given the transaction details, FX spread, and risk factors, perform anomaly screening. Check for unusual spread markups, rate manipulation indicators, structuring patterns, and sanctions concerns based on customer country. Respond JSON: {anomaly_detected: boolean, anomaly_type: string, anomaly_details: string, sanctions_flag: boolean, recommended_action: string, confidence: number}",
        "Transaction: {transaction_id}. {transaction_currency} {transaction_amount} -> {base_currency} {base_amount}. Processor rate: {processor_rate} vs mid-market: {mid_market_rate}. Spread: {spread_pct}%. FX impact: {fx_summary}. Risk: {risk_level} ({risk_factors}). Country: {customer_country}.",
        [
          { field: "anomaly_detected", as: "anomaly_detected", type: "string" },
          { field: "anomaly_type", as: "anomaly_type", type: "string" },
          { field: "anomaly_details", as: "anomaly_details", type: "string" },
          { field: "sanctions_flag", as: "sanctions_flag", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "confidence", as: "anomaly_confidence", type: "number" },
        ],
      ),

      // 5. Risk score condition
      n("5", "condition", "High risk or anomaly detected?", 1000, {
        conditionData: [
          {
            variable: "risk_level",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "anomaly_detected",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Fraud review escalation (Slack)
      slackNode(
        "6",
        "Slack: Fraud/risk review escalation",
        1240,
        ":rotating_light: FX Risk Alert\\nTransaction: {transaction_id}\\n{transaction_currency} {transaction_amount} -> {base_currency} {base_amount}\\nSpread: {spread_pct}% | Risk: {risk_level} (Score: {risk_score})\\nAnomaly: {anomaly_type} - {anomaly_details}\\nSanctions Flag: {sanctions_flag}\\nAction: {recommended_action}\\nCustomer: {customer_email} ({customer_country})",
      ),

      // 7. Exposure dashboard (Sheets)
      sheetsNode(
        "7",
        "Update exposure dashboard (Sheets)",
        1480,
        "FXExposure",
        '[["{txn_timestamp}","{transaction_id}","{transaction_currency}","{transaction_amount}","{base_currency}","{base_amount}","{processor_rate}","{mid_market_rate}","{spread_pct}","{fx_gain_loss}","{risk_level}","{anomaly_detected}"]]',
      ),

      // 8. Treasury summary (email)
      emailNode(
        "8",
        "Send treasury FX summary",
        1720,
        "{treasury_email}",
        "FX Transaction: {transaction_currency} {transaction_amount} - {risk_level} Risk",
        "FX Transaction Summary\\n\\nTransaction: {transaction_id}\\nAmount: {transaction_currency} {transaction_amount}\\nBase Equivalent: {base_currency} {base_amount}\\nFair Value: {base_currency} {fair_value_base}\\n\\nFX Details:\\n- Processor Rate: {processor_rate}\\n- Mid-Market Rate: {mid_market_rate}\\n- Spread: {spread_pct}% (Cost: {base_currency} {spread_cost_base})\\n- Gain/Loss: {base_currency} {fx_gain_loss}\\n\\nRisk Assessment: {risk_level} (Score: {risk_score})\\nRisk Factors: {risk_factors}\\n\\nAnomaly Screening: {anomaly_type}\\nDetails: {anomaly_details}\\nRecommended Action: {recommended_action}\\n\\nCustomer: {customer_email} ({customer_country})",
      ),

      // 9. Transaction audit logging
      sheetsNode(
        "9",
        "Log transaction audit trail (Sheets)",
        1960,
        "FXAuditLog",
        '[["{txn_timestamp}","{transaction_id}","{customer_email}","{customer_country}","{transaction_currency}","{transaction_amount}","{base_currency}","{base_amount}","{processor_rate}","{mid_market_rate}","{spread_pct}","{fx_gain_loss}","{risk_level}","{risk_score}","{anomaly_detected}","{anomaly_type}","{recommended_action}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high risk / anomaly -> Slack escalation
      e("5", "7", "output_2"), // normal -> straight to dashboard
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
